/**
 * Run-scoped write tools for the multi-hop executor agent.
 *
 * The executor owns the ReAct loop, but workbook writes still go through the
 * same `WorkbookModification` apply path as the legacy step runner. This file
 * defines strict focused model-facing tools plus the internal batch adapter
 * that validates, applies, and records results in the run accumulators.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import {
  collectFdsBoundFormulaSheets,
  findFqlDatePolicyViolation,
  isFdsBoundFormula,
} from "./formulaWriteDetection";
import {
  extractFdsFormulaArguments,
  extractFqlArgumentExpression,
} from "./fqlDatePolicy";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import type { WorksheetWorkloadOutputScope } from "./executorWorkload";
import type {
  CellFormat,
  ExecutorStepState,
  RangeModification,
  SheetModification,
  WorkbookModification,
  WorkbookOperation,
} from "./types";
import {
  normalizeMissingCellActions,
  readRangeContent,
} from "./excelService";
import { diag, isDiagEnabled } from "./aiDiag";
import {
  hasLiteralPercentToken,
  hasMultiplyingPercentToken,
  normalizeFactSetNumberFormat,
  type FactSetFormatHint,
} from "./factsetValueSemantics";
import {
  sameFactSetFormulaIdentity,
  type TrackedFactSetCell,
} from "./factsetFormulaFormatRegistry";
import { normalizeFactSetFormatsForModification } from "./factsetFormatEnforcement";

export { normalizeFactSetFormatsForModification } from "./factsetFormatEnforcement";
import { getNote } from "./excelHostOps";
import {
  buildSourceNoteAudit,
  citationCellKey,
  isGeneratedSourceNote,
  prepareCitationWrites,
  type CitationIdsMatrix,
  type CitationNoteTarget,
  type SourceNoteAudit,
} from "./citationNotes";
import {
  parseCellRangeAddress,
  parseSingleCellAddress,
} from "./excelCellAddress";
import { recordColumnSizingIntent } from "./columnSizingPolicy";

// ---------------------------------------------------------------------------
// JSON-schema helpers
// ---------------------------------------------------------------------------

/** JSON schema for optional string fields that must be provided as string or null. */
const nullableStringSchema = {
  anyOf: [{ type: "string" }, { type: "null" }],
};

/** Wrap a JSON schema as an explicit nullable value for strict tool decoding. */
function nullableSchema(schema: Record<string, unknown>): Record<string, unknown> {
  return { anyOf: [schema, { type: "null" }] };
}

/** JSON schema for scalar cell/table/range values accepted by workbook writes. */
const primitiveValueSchema = {
  type: ["string", "number", "boolean", "null"],
};

/** JSON schema for 2D value matrices used by range fills and table rows. */
const twoDimensionalValueArraySchema = {
  type: "array",
  minItems: 1,
  items: {
    type: "array",
    minItems: 1,
    items: primitiveValueSchema,
  },
};

/** JSON schema for 2D formula matrices used by range fills. */
const twoDimensionalFormulaArraySchema = {
  type: "array",
  minItems: 1,
  items: {
    type: "array",
    minItems: 1,
    items: {
      type: ["string", "null"],
    },
  },
};

/** Exact research citation IDs attached to one sourced cell. */
const citationIdsSchema = {
  type: "array",
  minItems: 1,
  description:
    "Exact research IDs for a sourced number or a substantive non-numeric claim. Metadata is not a claim: omit for company names, tickers, filing types/dates, periods, units, metric labels, headings, and derived formulas.",
  items: {
    type: "string",
    minLength: 1,
  },
};

/** Sparse row-major citation IDs aligned with one range write matrix. */
const citationIdsMatrixSchema = {
  type: "array",
  minItems: 1,
  description:
    "Sparse row-major citations aligned with the write matrix. Cite sourced number cells and substantive non-numeric claim cells; use null for row descriptors such as company, filing, period, metric, and units, plus headings and formulas.",
  items: {
    type: "array",
    minItems: 1,
    items: {
      anyOf: [
        { type: "null" },
        citationIdsSchema,
      ],
    },
  },
};

const borderStyleSchema = {
  type: "object",
  properties: {
    style: nullableSchema({
      type: "string",
      enum: ["none", "thin", "medium", "thick", "dashed", "dotted", "double"],
    }),
    color: nullableStringSchema,
  },
  required: ["style", "color"],
  additionalProperties: false,
};

/** Strict JSON schema for the model-facing `CellFormat` subset. */
const cellFormatSchema = {
  type: "object",
  properties: {
    font: nullableSchema({
      type: "object",
      properties: {
        name: nullableStringSchema,
        size: nullableSchema({ type: "number" }),
        color: nullableStringSchema,
        bold: nullableSchema({ type: "boolean" }),
        italic: nullableSchema({ type: "boolean" }),
        underline: nullableSchema({
          type: "string",
          enum: ["none", "single", "double"],
        }),
        strikethrough: nullableSchema({ type: "boolean" }),
      },
      required: ["name", "size", "color", "bold", "italic", "underline", "strikethrough"],
      additionalProperties: false,
    }),
    fill: nullableSchema({
      type: "object",
      properties: {
        color: nullableStringSchema,
        pattern: nullableSchema({ type: "string", enum: ["solid", "none"] }),
      },
      required: ["color", "pattern"],
      additionalProperties: false,
    }),
    borders: nullableSchema({
      type: "object",
      properties: {
        top: nullableSchema(borderStyleSchema),
        bottom: nullableSchema(borderStyleSchema),
        left: nullableSchema(borderStyleSchema),
        right: nullableSchema(borderStyleSchema),
      },
      required: ["top", "bottom", "left", "right"],
      additionalProperties: false,
    }),
    alignment: nullableSchema({
      type: "object",
      properties: {
        horizontal: nullableSchema({
          type: "string",
          enum: ["left", "center", "right", "fill", "justify"],
        }),
        vertical: nullableSchema({
          type: "string",
          enum: ["top", "center", "bottom"],
        }),
        wrapText: nullableSchema({ type: "boolean" }),
        indent: nullableSchema({ type: "number" }),
        textRotation: nullableSchema({ type: "number" }),
      },
      required: ["horizontal", "vertical", "wrapText", "indent", "textRotation"],
      additionalProperties: false,
    }),
    numberFormat: nullableSchema({
      type: "object",
      properties: {
        format: { type: "string" },
      },
      required: ["format"],
      additionalProperties: false,
    }),
  },
  required: ["font", "fill", "borders", "alignment", "numberFormat"],
  additionalProperties: false,
};

/**
 * JSON schema for the `WorkbookModification` surface exposed to the
 * multi-hop executor. This mirrors the model-facing write surface, not every
 * internal TypeScript field.
 */
const workbookModificationSchema = {
  type: "object",
  properties: {
    sheets: {
      type: "array",
      description:
        "Sheet structural operations exposed to the agent. `setVisibility` is intentionally not exposed here.",
      items: {
        type: "object",
        properties: {
          action: {
            type: "string",
            enum: ["create", "delete", "rename", "activate", "move"],
          },
          name: { type: "string" },
          newName: { type: "string" },
          move: {
            type: "object",
            properties: {
              newIndex: { type: "number" },
              sourceWorkbookName: { type: "string" },
              targetWorkbookName: { type: "string" },
              selectSheet: { type: "boolean" },
            },
            additionalProperties: false,
          },
        },
        additionalProperties: false,
      },
    },
    worksheets: {
      type: "array",
      description:
        "Per-worksheet edits. `shapes` is intentionally not exposed to the executor agent in this slice.",
      items: {
        type: "object",
        properties: {
          sheetName: { type: "string" },
          cells: {
            type: "array",
            items: {
              type: "object",
              properties: {
                address: { type: "string" },
                action: { type: "string", enum: ["set", "clear", "format"] },
                value: primitiveValueSchema,
                formula: { type: "string" },
                citationIds: {
                  ...citationIdsSchema,
                  description:
                    "Exact IDs for this sourced number or substantive non-numeric claim. Do not cite descriptive metadata such as company, filing type/date, period, metric, units, labels, or headings.",
                },
                format: cellFormatSchema,
                ttfQuery: { type: "string" },
              },
              additionalProperties: false,
            },
          },
          ranges: {
            type: "array",
            items: {
              type: "object",
              properties: {
                address: { type: "string" },
                action: {
                  type: "string",
                  enum: [
                    "fill",
                    "clear",
                    "format",
                    "sort",
                    "autofit",
                    "resize",
                    "deleteRange",
                    "insertRange",
                    "moveRange",
                    "hideColumns",
                    "hideRows",
                    "showColumns",
                    "showRows",
                    "select",
                  ],
                },
                values: twoDimensionalValueArraySchema,
                formulas: twoDimensionalFormulaArraySchema,
                citationIdsMatrix: {
                  ...citationIdsMatrixSchema,
                  description:
                    "Row-major null-or-ID arrays aligned exactly with values/formulas. In a descriptor-plus-number row, cite only the number cell. Use null for company, filing, period, metric, units, labels, headings, formulas, and duplicate nearby facts.",
                },
                format: cellFormatSchema,
                clear: {
                  type: "object",
                  properties: {
                    clearType: {
                      type: "string",
                      enum: ["Comments", "Contents", "Formats", "Hyperlinks", "Notes", "Outline", "All"],
                    },
                  },
                  additionalProperties: false,
                },
                sort: {
                  type: "object",
                  properties: {
                    sortKeys: { type: "array", items: { type: "object", additionalProperties: true } },
                    options: { type: "object", additionalProperties: true },
                  },
                  additionalProperties: false,
                },
                fitColumns: { type: "boolean" },
                fitRows: { type: "boolean" },
                width: { type: "number" },
                height: { type: "number" },
                deleteRange: {
                  type: "object",
                  properties: {
                    shiftDirection: { type: "string", enum: ["left", "up"] },
                  },
                  additionalProperties: false,
                },
                insertRange: {
                  type: "object",
                  properties: {
                    shiftDirection: { type: "string", enum: ["right", "down"] },
                    copyOrigin: {
                      type: "string",
                      enum: ["leftOrAbove", "rightOrBelow"],
                    },
                  },
                  additionalProperties: false,
                },
                moveRange: {
                  type: "object",
                  properties: {
                    destination: { type: "string" },
                    preserveSource: { type: "boolean" },
                    sourceSheetName: { type: "string" },
                    destinationSheetName: { type: "string" },
                  },
                  additionalProperties: false,
                },
              },
              additionalProperties: false,
            },
          },
          logos: {
            type: "array",
            items: {
              type: "object",
              properties: {
                companyName: { type: "string" },
                address: { type: "string" },
                width: { type: "number" },
                height: { type: "number" },
              },
              additionalProperties: false,
            },
          },
        },
        additionalProperties: false,
      },
    },
    workbookOperations: {
      type: "array",
      items: {
        type: "object",
        properties: {
          action: {
            type: "string",
            enum: [
              "addWorkbook",
              "copyWorksheet",
              "insertWorksheetsFromBase64",
              "recalculate",
              "refreshFactSetData",
              "refreshCalculate",
              "getRefreshStatus",
              "getRefresh",
              "awaitRefreshCalculate",
              "addNamedObject",
              "removeNamedObject",
              "search",
            ],
          },
          copyWorksheet: { type: "object", additionalProperties: true },
          insertWorksheetsFromBase64: { type: "object", additionalProperties: true },
          recalculate: { type: "object", additionalProperties: true },
          refreshCalculate: { type: "object", additionalProperties: true },
          getRefreshStatus: { type: "object", additionalProperties: true },
          getRefresh: { type: "object", additionalProperties: true },
          awaitRefreshCalculate: { type: "object", additionalProperties: true },
          addNamedObject: {
            type: "object",
            description:
              "Create a workbook-level named range/formula. Requires `name` and either `refersTo` or `address`; do not use `reference`.",
            properties: {
              name: { type: "string" },
              refersTo: { type: "string" },
              address: { type: "string" },
              nameType: { type: "string" },
              workbookName: { type: "string" },
              scope: { type: "number" },
              isVisible: { type: "boolean" },
              visible: { type: "boolean" },
              comment: { type: "string" },
            },
            required: ["name"],
            additionalProperties: false,
          },
          removeNamedObject: { type: "object", additionalProperties: true },
          search: { type: "object", additionalProperties: true },
        },
        additionalProperties: false,
      },
    },
    message: { type: "string" },
  },
  additionalProperties: false,
};

// ---------------------------------------------------------------------------
// Tool definition
// ---------------------------------------------------------------------------

/** Internal compatibility definition; intentionally absent from the model registry. */
export const MODIFY_WORKBOOK_TOOL: ChartCapabilityToolDef = {
  name: "modify_workbook",
  description:
    "Apply a batch of workbook modifications immediately. Use this for concrete Excel writes " +
    "after observing workbook state. Pass an existing stepId when the write belongs to a run " +
    "step; use the exact id from the current run steps and do not invent ids for existing steps.",
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        ...nullableStringSchema,
        description:
          "Existing run step id this write completes or advances. Reuse the exact id shown in the current run steps; pass null only for run-level fixes not tied to a step.",
      },
      targetSheet: {
        ...nullableStringSchema,
        description:
          "Optional sheet hint for pre-apply activation. Prefer the sheet observed via list_sheets/read_range, not a stale targetSheet.",
      },
      modifications: {
        ...workbookModificationSchema,
        description:
          "WorkbookModification payload using the same exposed surface as the legacy executor.",
      },
    },
    required: ["stepId", "targetSheet", "modifications"],
    additionalProperties: false,
  },
};

const legacyWriteStepProperties = {
  stepId: {
    ...nullableStringSchema,
    description:
      "Existing run step id this write belongs to. Use the exact id from the current run steps, or null for run-level fixes.",
  },
};

const sheetNameProperty = {
  type: "string",
  description: "Resolved worksheet name from list_sheets/read_range.",
};

const rangeAddressProperty = {
  type: "string",
  description:
    "A1-notation range address to modify. For value/formula writes, the matrix must contain exactly one entry per addressed cell; values are never broadcast, padded, or truncated.",
};

/** Focused tool for writing a rectangular value matrix. */
export const SET_RANGE_VALUES_TOOL: ChartCapabilityToolDef = {
  name: "set_range_values",
  description:
    "Set values in a single worksheet range. The rectangular values matrix dimensions must exactly match the target address; the runtime never broadcasts, pads, or truncates it. In citationIdsMatrix, cite sourced number cells and substantive non-numeric claims only. Descriptive metadata is not a claim: company, filing type/date, period, metric, units, labels, and headings stay null. Never use guessed values to replace FactSet formulas or factual source data.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      values: twoDimensionalValueArraySchema,
      citationIdsMatrix: nullableSchema(citationIdsMatrixSchema),
    },
    required: ["sheetName", "address", "values", "citationIdsMatrix"],
    additionalProperties: false,
  },
};

/**
 * Native ReAct value-write surface.
 *
 * Citation evidence is attached separately through `set_citations`, so native
 * value writes do not expose the legacy citation-ID matrix.
 */
export const NATIVE_SET_RANGE_VALUES_TOOL: ChartCapabilityToolDef = {
  name: SET_RANGE_VALUES_TOOL.name,
  description:
    "Set values in a single worksheet range. The rectangular values matrix dimensions must exactly match the target address; the runtime never broadcasts, pads, or truncates it. This tool writes static values only; attach FactSet AI source metadata separately with set_citations. Never use guessed values to replace factual source data.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      values: twoDimensionalValueArraySchema,
    },
    required: ["sheetName", "address", "values"],
    additionalProperties: false,
  },
};

/** Focused tool for writing a rectangular formula matrix. */
export const SET_RANGE_FORMULAS_TOOL: ChartCapabilityToolDef = {
  name: "set_range_formulas",
  description:
    "Set a non-empty rectangular formula matrix in a single worksheet range. The matrix dimensions must exactly match the target address; the runtime never broadcasts, pads, or truncates it. Use generate_formula first for FactSet/FDS formulas unless the exact formula is already known. Formula cells are sourced by their formula text and are not citation targets. Never pass formulas: [] or use literal-only formulas as placeholder source data.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      formulas: twoDimensionalFormulaArraySchema,
    },
    required: ["sheetName", "address", "formulas"],
    additionalProperties: false,
  },
};

/** Focused tool for formatting a single worksheet range. */
export const FORMAT_RANGE_TOOL: ChartCapabilityToolDef = {
  name: "format_range",
  description:
    "Apply formatting to a single worksheet range without changing its values or formulas.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      format: cellFormatSchema,
    },
    required: ["sheetName", "address", "format"],
    additionalProperties: false,
  },
};

/** Focused tool for creating a worksheet. */
export const CREATE_SHEET_TOOL: ChartCapabilityToolDef = {
  name: "create_sheet",
  description: "Create a worksheet, optionally activating it after creation.",
  inputSchema: {
    type: "object",
    properties: {
      ...legacyWriteStepProperties,
      name: { type: "string", description: "Worksheet name to create." },
      activate: {
        type: "boolean",
        description: "Whether to activate the new worksheet after creation.",
      },
    },
    required: ["stepId", "name", "activate"],
    additionalProperties: false,
  },
};

/** Focused tool for renaming a worksheet. */
export const RENAME_SHEET_TOOL: ChartCapabilityToolDef = {
  name: "rename_sheet",
  description: "Rename an existing worksheet.",
  inputSchema: {
    type: "object",
    properties: {
      ...legacyWriteStepProperties,
      name: { type: "string", description: "Existing worksheet name." },
      newName: { type: "string", description: "New worksheet name." },
    },
    required: ["stepId", "name", "newName"],
    additionalProperties: false,
  },
};

/** Focused tool for activating a worksheet. */
export const ACTIVATE_SHEET_TOOL: ChartCapabilityToolDef = {
  name: "activate_sheet",
  description: "Activate an existing worksheet.",
  inputSchema: {
    type: "object",
    properties: {
      ...legacyWriteStepProperties,
      name: { type: "string", description: "Worksheet name to activate." },
    },
    required: ["stepId", "name"],
    additionalProperties: false,
  },
};

/** Clear cell content or formatting without shifting surrounding cells. */
export const CLEAR_RANGE_TOOL: ChartCapabilityToolDef = {
  name: "clear_range",
  description:
    "Clear one worksheet range without shifting cells. Choose Contents, Formats, Hyperlinks, Outline, or All.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      clearType: {
        type: "string",
        enum: ["Contents", "Formats", "Hyperlinks", "Outline", "All"],
      },
    },
    required: ["sheetName", "address", "clearType"],
    additionalProperties: false,
  },
};

/** Insert, delete, move, or copy a rectangular range. */
export const EDIT_RANGE_STRUCTURE_TOOL: ChartCapabilityToolDef = {
  name: "edit_range_structure",
  description:
    "Perform one structural range operation. insert/delete shift surrounding cells; move/copy require destinationAddress. Pass null for fields not used by the selected operation.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      operation: { type: "string", enum: ["insert", "delete", "move", "copy"] },
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      destinationAddress: nullableSchema({
        type: "string",
        description:
          "Unqualified single-cell top-left destination anchor for move/copy, such as F2. Never pass a rectangular range; use destinationSheetName for cross-sheet routing.",
      }),
      destinationSheetName: nullableStringSchema,
      shiftDirection: nullableSchema({
        type: "string",
        enum: ["left", "up", "right", "down"],
      }),
      copyOrigin: nullableSchema({
        type: "string",
        enum: ["leftOrAbove", "rightOrBelow"],
      }),
    },
    required: [
      "operation",
      "sheetName",
      "address",
      "destinationAddress",
      "destinationSheetName",
      "shiftDirection",
      "copyOrigin",
    ],
    additionalProperties: false,
  },
};

/** Change row/column sizing or visibility without changing cell content. */
export const LAYOUT_RANGE_TOOL: ChartCapabilityToolDef = {
  name: "layout_range",
  description:
    "Apply one range-axis layout operation: autofit, explicit width/height, or hide/show rows/columns. Pass size only for explicit sizing.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      operation: {
        type: "string",
        enum: [
          "autofit_columns",
          "autofit_rows",
          "set_column_width",
          "set_row_height",
          "hide_columns",
          "show_columns",
          "hide_rows",
          "show_rows",
        ],
      },
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      size: nullableSchema({ type: "number" }),
    },
    required: ["operation", "sheetName", "address", "size"],
    additionalProperties: false,
  },
};

const sortKeySchema = {
  type: "object",
  properties: {
    key: {
      type: "string",
      description:
        "One unqualified A1 cell inside the sort address, such as B2 for A1:D6. Never pass a range such as B2:B6.",
    },
    order: nullableSchema({
      type: "string",
      enum: ["Ascending", "Descending"],
    }),
    dataOption: nullableSchema({
      type: "string",
      enum: ["Normal", "TextAsNumbers"],
    }),
  },
  required: ["key", "order", "dataOption"],
  additionalProperties: false,
};

/** Sort one rectangular range by one to three explicit keys. */
export const SORT_RANGE_TOOL: ChartCapabilityToolDef = {
  name: "sort_range",
  description:
    "Sort one rectangular range using one to three keys. Each key must be a single cell inside the sort address (for example B2 within A1:D6), never a range such as B2:B6. Nullable options use Excel defaults.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      sortKeys: {
        type: "array",
        minItems: 1,
        maxItems: 3,
        items: sortKeySchema,
      },
      header: nullableSchema({ type: "string", enum: ["Guess", "Yes", "No"] }),
      matchCase: nullableSchema({ type: "boolean" }),
      orientation: nullableSchema({
        type: "string",
        enum: ["TopToBottom", "LeftToRight"],
      }),
      sortMethod: nullableSchema({
        type: "string",
        enum: ["PinYin", "Stroke"],
      }),
    },
    required: [
      "sheetName",
      "address",
      "sortKeys",
      "header",
      "matchCase",
      "orientation",
      "sortMethod",
    ],
    additionalProperties: false,
  },
};

/** Create, rename, delete, move, or activate one worksheet. */
export const MANAGE_WORKSHEET_TOOL: ChartCapabilityToolDef = {
  name: "manage_worksheet",
  description:
    "Manage one worksheet tab. Pass null for action-specific fields that are not used.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      operation: {
        type: "string",
        enum: ["create", "rename", "delete", "move", "activate"],
      },
      name: { type: "string" },
      newName: nullableStringSchema,
      newIndex: nullableSchema({ type: "number" }),
      sourceWorkbookName: nullableStringSchema,
      targetWorkbookName: nullableStringSchema,
      activate: { type: "boolean" },
    },
    required: [
      "operation",
      "name",
      "newName",
      "newIndex",
      "sourceWorkbookName",
      "targetWorkbookName",
      "activate",
    ],
    additionalProperties: false,
  },
};

/** Copy a worksheet using the host copy operation. */
export const COPY_WORKSHEET_TOOL: ChartCapabilityToolDef = {
  name: "copy_worksheet",
  description:
    "Copy a worksheet in the active pinned workbook. Pass null for location to accept the host default.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sourceSheetName: { type: "string" },
      location: nullableSchema({ type: "string", enum: ["before", "after"] }),
    },
    required: ["sourceSheetName", "location"],
    additionalProperties: false,
  },
};

/** Create/update or remove a workbook named range/formula. */
export const MANAGE_NAMED_RANGE_TOOL: ChartCapabilityToolDef = {
  name: "manage_named_range",
  description:
    "Create/update or delete a workbook named range/formula. refersTo is required for create_or_update and null for delete.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      operation: { type: "string", enum: ["create_or_update", "delete"] },
      name: { type: "string" },
      refersTo: nullableStringSchema,
      workbookName: nullableStringSchema,
      scope: nullableSchema({ type: "number" }),
      visible: nullableSchema({ type: "boolean" }),
      comment: nullableStringSchema,
    },
    required: [
      "operation",
      "name",
      "refersTo",
      "workbookName",
      "scope",
      "visible",
      "comment",
    ],
    additionalProperties: false,
  },
};

/** Insert one company logo at a worksheet cell. */
export const INSERT_COMPANY_LOGO_TOOL: ChartCapabilityToolDef = {
  name: "insert_company_logo",
  description:
    "Insert a company logo at one worksheet cell. Pass null dimensions to use host defaults.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      sheetName: sheetNameProperty,
      address: rangeAddressProperty,
      companyName: { type: "string" },
      width: nullableSchema({ type: "number" }),
      height: nullableSchema({ type: "number" }),
    },
    required: ["sheetName", "address", "companyName", "width", "height"],
    additionalProperties: false,
  },
};

/** Model-facing write tools; the batch adapter remains internal-only. */
export const EXECUTOR_WRITE_TOOLS: readonly ChartCapabilityToolDef[] = [
  SET_RANGE_VALUES_TOOL,
  SET_RANGE_FORMULAS_TOOL,
  FORMAT_RANGE_TOOL,
  CLEAR_RANGE_TOOL,
  EDIT_RANGE_STRUCTURE_TOOL,
  LAYOUT_RANGE_TOOL,
  SORT_RANGE_TOOL,
  MANAGE_WORKSHEET_TOOL,
  COPY_WORKSHEET_TOOL,
  MANAGE_NAMED_RANGE_TOOL,
  INSERT_COMPANY_LOGO_TOOL,
];

// ---------------------------------------------------------------------------
// Public input/result contracts
// ---------------------------------------------------------------------------

/** Validated input for `modify_workbook`. */
export interface ModifyWorkbookInput {
  /** Existing plan step id, or null for run-level workbook writes. */
  stepId: string | null;
  /** Optional sheet hint used by the host apply callback. */
  targetSheet: string | null;
  /** Workbook modifications to apply immediately. */
  modifications: WorkbookModification;
}

/** Result returned by `modify_workbook`. */
export interface ModifyWorkbookResult {
  /** True when the tool arguments were valid and the apply callback was reached. */
  ok: boolean;
  /** True when the host reported a successful apply. */
  applied?: boolean;
  /** Existing plan step id associated with this write, if any. */
  stepId?: string | null;
  /** Human-readable apply result or validation error. */
  message?: string;
  /** Error message for invalid input or thrown apply failures. */
  error?: string;
  /** Host operation log returned by the apply path. */
  operations?: string[];
  /** Edited addresses returned by the apply path. */
  editedCells?: string[];
  /** Host-declined actions, if any. */
  skippedActions?: string[];
  /** Host mutation attempts that failed after being attempted. */
  failedActions?: string[];
  /** The host call failed after dispatch, so workbook mutation is uncertain. */
  ambiguousWrite?: boolean;
  /** Recoverable host-not-ready reason, when apply paused. */
  retryReason?: "stuck_edit" | "stuck_busy";
  /** Whether mutation dispatch began before a retryable host-ready pause. */
  hostDispatchAttempted?: boolean;
  /** Whether this write requested trailing refresh-all finalization. */
  deferredRefreshAll?: boolean;
  /** Deferred recalc mode requested by this write. */
  deferredRecalcMode?: "recalculate" | "full" | "fullRebuild";
  /** Exact read-back result for generated citation Notes. */
  sourceNoteAudit?: SourceNoteAudit;
  /** Stable machine-readable validation/recovery code. */
  code?: "range_matrix_shape_mismatch";
  /** Whether the model may make one corrected call in this run. */
  retryable?: boolean;
  /** Required retry surface for a correctable validation error. */
  retryWith?: "same_tool";
  /** Matrix field whose dimensions did not match the target. */
  matrixField?: "values" | "formulas";
  /** Normalized range whose matrix failed shape preflight. */
  targetAddress?: string;
  /** Shape parsed from the requested A1 address. */
  expectedShape?: MatrixShape;
  /** Shape supplied by the model. */
  actualShape?: MatrixShape;
  /** Concrete correction instruction for the executor. */
  instruction?: string;
}

export interface ModifyWorkbookValidationContext {
  userRequest?: string;
  intentText?: string;
  fdsFormulaWriteTargets?: readonly { sheetName: string; address: string }[];
  fdsDateAxisWriteTargets?: readonly {
    sheetName: string;
    address: string;
    kind: "serial" | "key";
  }[];
  /**
   * FactSet outputs whose value scale is a percentage, from run provenance.
   * Percent-scale cells are display-formatted with a percent sign rather than
   * the raw two-decimal market-price default, so the price-precision rule
   * must not claim them.
   */
  factSetPercentScaleTargets?: readonly { sheetName: string; address: string }[];
  priorModifications?: WorkbookModification | null;
}

type PrimitiveCellValue = string | number | boolean | null;

interface MatrixShape {
  rows: number;
  columns: number;
}

interface RangeMatrixShapeIssue {
  location: string;
  address: string;
  field: "values" | "formulas";
  expectedShape: MatrixShape;
  actualShape: MatrixShape;
}

const RANGE_MATRIX_SHAPE_MISMATCH_CODE = "range_matrix_shape_mismatch" as const;

interface GranularRangeBaseInput {
  stepId: string | null;
  sheetName: string;
  address: string;
}

interface SetRangeValuesInput extends GranularRangeBaseInput {
  values: PrimitiveCellValue[][];
  citationIdsMatrix?: CitationIdsMatrix;
}

interface SetRangeFormulasInput extends GranularRangeBaseInput {
  formulas: (string | null)[][];
}

interface FormatRangeInput extends GranularRangeBaseInput {
  format: CellFormat;
}

interface CreateSheetInput {
  stepId: string | null;
  name: string;
  activate: boolean;
}

interface RenameSheetInput {
  stepId: string | null;
  name: string;
  newName: string;
}

interface ActivateSheetInput {
  stepId: string | null;
  name: string;
}

// ---------------------------------------------------------------------------
// Executor
// ---------------------------------------------------------------------------

export const WORKBOOK_EDIT_STEPS_REQUIRED_ERROR =
  "Workbook edits require the internal native run step.";

interface ScopedAddress {
  sheetName: string | undefined;
  range: ParsedCellRange | undefined;
  hasConflictingSheet: boolean;
}

function normalizeSheetName(sheetName: string | undefined): string {
  return sheetName?.trim().toLocaleLowerCase() ?? "";
}

function sameSheetName(
  left: string | undefined,
  right: string | undefined,
): boolean {
  const normalizedLeft = normalizeSheetName(left);
  return normalizedLeft.length > 0 && normalizedLeft === normalizeSheetName(right);
}

function parseAddressSheetPrefix(prefix: string): string | undefined {
  const trimmed = prefix.trim();
  if (!trimmed || trimmed.includes("[") || trimmed.includes("]")) {
    return undefined;
  }
  if (trimmed.startsWith("'") || trimmed.endsWith("'")) {
    if (!(trimmed.startsWith("'") && trimmed.endsWith("'"))) {
      return undefined;
    }
    return trimmed.slice(1, -1).replace(/''/g, "'");
  }
  return trimmed.includes("'") ? undefined : trimmed;
}

function parseScopedAddress(
  address: string,
  fallbackSheetName: string | undefined,
): ScopedAddress {
  const trimmed = address.trim();
  const separator = trimmed.lastIndexOf("!");
  const explicitSheetName =
    separator >= 0
      ? parseAddressSheetPrefix(trimmed.slice(0, separator))
      : undefined;
  const rangeText = separator >= 0 ? trimmed.slice(separator + 1) : trimmed;
  const hasConflictingSheet =
    separator >= 0 &&
    explicitSheetName !== undefined &&
    fallbackSheetName !== undefined &&
    !sameSheetName(explicitSheetName, fallbackSheetName);
  return {
    sheetName: separator >= 0 ? explicitSheetName : fallbackSheetName?.trim(),
    range: parseCellRange(rangeText),
    hasConflictingSheet,
  };
}

function formatScopedStepId(stepId: string | null): string {
  return stepId === null ? "null" : `"${stepId}"`;
}

function resolveWorksheetWorkloadOutputScope(
  ctx: ExecutorToolRuntimeContext,
): WorksheetWorkloadOutputScope | undefined {
  if (ctx.idLookupOutputScope) {
    return {
      ...ctx.idLookupOutputScope,
      workloadLabel: "ID lookup workload",
    };
  }
  return ctx.worksheetWorkloadOutputScope;
}

function findWorksheetWorkloadOutputScopeError(
  ctx: ExecutorToolRuntimeContext,
  input: ModifyWorkbookInput,
): string | null {
  const scope = resolveWorksheetWorkloadOutputScope(ctx);
  if (!scope) return null;

  const protectedSheet = scope.sheetName?.trim();
  const workloadLabel = scope.workloadId?.trim() || "(unknown workload)";
  const workloadType = scope.workloadLabel?.trim() || "Worksheet workload";
  const targetReference = parseScopedAddress(
    scope.targetAddress ?? "",
    protectedSheet,
  );
  const target =
    !targetReference.hasConflictingSheet &&
    sameSheetName(targetReference.sheetName, protectedSheet)
      ? targetReference.range
      : undefined;

  const invalidScopeError = (): string =>
    `${workloadType} "${workloadLabel}" has an invalid protected target ` +
    `${protectedSheet || "(unknown sheet)"}!${scope.targetAddress ?? ""}. ` +
    "Content writes on that sheet are blocked until the workload scope is valid.";

  const wrongStepError = (label: string): string =>
    `${workloadType} "${workloadLabel}" reserves content writes on ` +
    `${protectedSheet}!${scope.targetAddress} for owner step ` +
    `${formatScopedStepId(scope.ownerStepId)}. Refusing ${label} from step ` +
    `${formatScopedStepId(input.stepId)}.`;

  const outsideTargetError = (label: string, address: string): string =>
    `${workloadType} "${workloadLabel}" limits owner-step content writes on ` +
    `${protectedSheet} to the exact target rectangle ${scope.targetAddress}. ` +
    `Refusing ${label} at ${address} because every row and column bound must ` +
    "be fully contained inside that rectangle.";

  const invalidAddressError = (label: string, address: string): string =>
    `${workloadType} "${workloadLabel}" cannot safely validate ${label} at ` +
    `"${address}". A finite A1 cell or range address is required.`;

  const unresolvedSheetError = (label: string): string =>
    `${workloadType} "${workloadLabel}" cannot safely validate ${label} ` +
    "because its worksheet target is unresolved.";

  const validateBounds = (
    sheetName: string | undefined,
    bounds: ParsedCellRange,
    label: string,
    displayAddress: string,
  ): string | null => {
    if (!sameSheetName(sheetName, protectedSheet)) return null;
    if (input.stepId !== scope.ownerStepId) return wrongStepError(label);
    if (!target) return invalidScopeError();
    if (
      bounds.startRow < target.startRow ||
      bounds.endRow > target.endRow ||
      bounds.startColumn < target.startColumn ||
      bounds.endColumn > target.endColumn
    ) {
      return outsideTargetError(label, displayAddress);
    }
    return null;
  };

  const validateAddress = (
    address: string,
    fallbackSheetName: string | undefined,
    label: string,
  ): string | null => {
    const parsed = parseScopedAddress(address, fallbackSheetName);
    if (parsed.hasConflictingSheet) {
      return (
        `${workloadType} "${workloadLabel}" refuses ${label} with a ` +
        `sheet-qualified address that conflicts with its enclosing worksheet: ` +
        `"${address}".`
      );
    }
    if (!parsed.sheetName) return unresolvedSheetError(label);
    if (!parsed.range) return invalidAddressError(label, address);
    return validateBounds(parsed.sheetName, parsed.range, label, address);
  };

  const rejectUnboundedProtectedMutation = (
    sheetName: string | undefined,
    label: string,
  ): string | null => {
    if (!sheetName) return unresolvedSheetError(label);
    if (!sameSheetName(sheetName, protectedSheet)) return null;
    if (input.stepId !== scope.ownerStepId) return wrongStepError(label);
    if (!target) return invalidScopeError();
    return (
      `${workloadType} "${workloadLabel}" refuses ${label} on ` +
      `${protectedSheet} because its affected cell rectangle cannot be proven ` +
      `to stay inside ${scope.targetAddress}.`
    );
  };

  for (const sheet of input.modifications.sheets ?? []) {
    if (
      (sheet.action === "delete" || sheet.action === "rename") &&
      sameSheetName(sheet.name, protectedSheet)
    ) {
      const error = rejectUnboundedProtectedMutation(
        sheet.name,
        `worksheet ${sheet.action}`,
      );
      if (error) return error;
    }
  }

  for (const worksheet of input.modifications.worksheets ?? []) {
    const worksheetName = worksheet.sheetName?.trim();

    for (const cell of worksheet.cells ?? []) {
      const mutatesContent =
        cell.action === "clear" ||
        (cell.action === "set" &&
          (cell.value !== undefined || cell.formula !== undefined));
      if (!mutatesContent) continue;
      const error = validateAddress(
        cell.address,
        worksheetName,
        `cell ${cell.action}`,
      );
      if (error) return error;
    }

    for (const range of worksheet.ranges ?? []) {
      if (range.action === "moveRange") {
        const sourceSheet =
          range.moveRange?.sourceSheetName?.trim() || worksheetName;
        const source = parseScopedAddress(range.address, sourceSheet);
        if (
          source.hasConflictingSheet ||
          !source.sheetName ||
          !source.range
        ) {
          return source.hasConflictingSheet
            ? `${workloadType} "${workloadLabel}" refuses moveRange with a sheet-qualified source that conflicts with sourceSheetName: "${range.address}".`
            : !source.sheetName
              ? unresolvedSheetError("moveRange source")
              : invalidAddressError("moveRange source", range.address);
        }
        if (range.moveRange?.preserveSource !== true) {
          const sourceError = validateBounds(
            source.sheetName,
            source.range,
            "moveRange source clear",
            range.address,
          );
          if (sourceError) return sourceError;
        }

        const destination = range.moveRange?.destination;
        if (!destination) {
          const error = rejectUnboundedProtectedMutation(
            range.moveRange?.destinationSheetName?.trim() || worksheetName,
            "moveRange destination",
          );
          if (error) return error;
          continue;
        }
        const destinationSheet =
          range.moveRange?.destinationSheetName?.trim() || worksheetName;
        const parsedDestination = parseScopedAddress(
          destination,
          destinationSheet,
        );
        if (
          parsedDestination.hasConflictingSheet ||
          !parsedDestination.sheetName ||
          !parsedDestination.range
        ) {
          return parsedDestination.hasConflictingSheet
            ? `${workloadType} "${workloadLabel}" refuses moveRange with a sheet-qualified destination that conflicts with destinationSheetName: "${destination}".`
            : !parsedDestination.sheetName
              ? unresolvedSheetError("moveRange destination")
              : invalidAddressError("moveRange destination", destination);
        }
        const rowCount = source.range.endRow - source.range.startRow + 1;
        const columnCount =
          source.range.endColumn - source.range.startColumn + 1;
        const destinationBounds: ParsedCellRange = {
          startRow: parsedDestination.range.startRow,
          endRow: parsedDestination.range.startRow + rowCount - 1,
          startColumn: parsedDestination.range.startColumn,
          endColumn:
            parsedDestination.range.startColumn + columnCount - 1,
        };
        const destinationError = validateBounds(
          parsedDestination.sheetName,
          destinationBounds,
          "moveRange destination write",
          destination,
        );
        if (destinationError) return destinationError;
        continue;
      }

      const mutatesAddressedContent =
        (range.action === "fill" &&
          (range.values !== undefined || range.formulas !== undefined)) ||
        (range.action === "clear" &&
          ["Contents", "All"].includes(range.clear?.clearType ?? "All")) ||
        range.action === "sort";
      if (mutatesAddressedContent) {
        const error = validateAddress(
          range.address,
          worksheetName,
          `range ${range.action}`,
        );
        if (error) return error;
        continue;
      }

      if (
        range.action === "deleteRange" ||
        range.action === "insertRange"
      ) {
        const parsed = parseScopedAddress(range.address, worksheetName);
        if (parsed.hasConflictingSheet) {
          return `${workloadType} "${workloadLabel}" refuses range ${range.action} with a sheet-qualified address that conflicts with its enclosing worksheet: "${range.address}".`;
        }
        const error = rejectUnboundedProtectedMutation(
          parsed.sheetName,
          `range ${range.action}`,
        );
        if (error) return error;
      }
    }

    for (const table of worksheet.tables ?? []) {
      if (
        table.action === "create" &&
        table.address &&
        Array.isArray(table.headers) &&
        table.headers.length > 0 &&
        Array.isArray(table.rows)
      ) {
        const anchor = parseScopedAddress(table.address, worksheetName);
        const rectangularRows = table.rows.every(
          (row) =>
            Array.isArray(row) && row.length === table.headers!.length,
        );
        if (
          anchor.hasConflictingSheet ||
          !anchor.sheetName ||
          !anchor.range ||
          anchor.range.startRow !== anchor.range.endRow ||
          anchor.range.startColumn !== anchor.range.endColumn ||
          !rectangularRows
        ) {
          const error = rejectUnboundedProtectedMutation(
            anchor.sheetName ?? worksheetName,
            "table create",
          );
          if (error) return error;
          continue;
        }
        const tableBounds: ParsedCellRange = {
          startRow: anchor.range.startRow,
          endRow: anchor.range.startRow + table.rows.length,
          startColumn: anchor.range.startColumn,
          endColumn:
            anchor.range.startColumn + table.headers.length - 1,
        };
        const error = validateBounds(
          anchor.sheetName,
          tableBounds,
          "table create",
          table.address,
        );
        if (error) return error;
      } else {
        const error = rejectUnboundedProtectedMutation(
          worksheetName,
          `table ${table.action}`,
        );
        if (error) return error;
      }
    }
  }

  for (const operation of input.modifications.workbookOperations ?? []) {
    if (
      operation.action === "recalculate" ||
      operation.action === "refreshFactSetData" ||
      operation.action === "refreshCalculate" ||
      operation.action === "insertTtfApiResponse"
    ) {
      const error = rejectUnboundedProtectedMutation(
        protectedSheet,
        `workbook operation ${operation.action}`,
      );
      if (error) return error;
    }
  }

  return null;
}

/**
 * Validates and applies a `modify_workbook` tool call.
 *
 * @param ctx - Run-scoped executor context with apply callback and accumulators.
 * @param rawInput - Parsed LLM arguments for `modify_workbook`.
 * @returns JSON string conforming to {@link ModifyWorkbookResult}.
 */
export async function executeModifyWorkbookTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const parsedInput = normaliseModifyWorkbookInput(rawInput);
  const structureError = validateModifyWorkbookStructure(parsedInput);
  if (structureError) {
    return JSON.stringify({
      ok: false,
      applied: false,
      stepId: parsedInput.stepId,
      error: structureError,
    } satisfies ModifyWorkbookResult);
  }
  const normalizedInput: ModifyWorkbookInput = {
    ...parsedInput,
    modifications: normalizeMissingCellActions(parsedInput.modifications),
  };
  return applyValidatedWorkbookModification(ctx, normalizedInput);
}

/**
 * Apply one already-typed workbook modification through the shared safety,
 * citation, write-guard, telemetry, and accumulator pipeline.
 *
 * Focused model-facing tools translate their strict inputs into this internal
 * contract. The legacy batch parser calls the same adapter for compatibility,
 * but `modify_workbook` itself is not published to the model.
 *
 * @param ctx - Run-scoped executor context with host and safety state.
 * @param normalizedInput - Typed modification assembled by trusted tool code.
 * @returns Model-facing JSON acknowledgement for the focused caller.
 */
export async function applyValidatedWorkbookModification(
  ctx: ExecutorToolRuntimeContext,
  normalizedInput: ModifyWorkbookInput,
): Promise<string> {
  const structureError = validateModifyWorkbookStructure(normalizedInput);
  if (structureError) {
    return JSON.stringify({
      ok: false,
      applied: false,
      stepId: normalizedInput.stepId,
      error: structureError,
    } satisfies ModifyWorkbookResult);
  }
  const step = resolveStep(ctx, normalizedInput.stepId);
  const shapeIssue = findRangeMatrixShapeIssue(normalizedInput.modifications);
  if (shapeIssue) {
    return rangeMatrixShapeError(
      normalizedInput.stepId,
      shapeIssue,
      "modify_workbook",
    );
  }
  const validationError = validateModifyWorkbookInput(normalizedInput, {
    userRequest: ctx.session.userRequest,
    intentText: `${ctx.planState.summary ?? ""} ${step?.planStep.description ?? ""}`,
    fdsFormulaWriteTargets: ctx.accumulators.fdsFormulaWriteTargets,
    fdsDateAxisWriteTargets: ctx.accumulators.fdsDateAxisWriteTargets,
    factSetPercentScaleTargets: collectFactSetPercentScaleTargets(ctx),
    priorModifications: ctx.accumulators.lastModifications,
  });
  if (validationError) {
    return JSON.stringify({
      ok: false,
      applied: false,
      stepId: normalizedInput.stepId,
      error: validationError,
    } satisfies ModifyWorkbookResult);
  }
  removeRedundantTtfQueriesFromResolvedFormulas(normalizedInput.modifications);

  if (ctx.planState.steps.length === 0) {
    return JSON.stringify({
      ok: false,
      applied: false,
      stepId: normalizedInput.stepId,
      error: WORKBOOK_EDIT_STEPS_REQUIRED_ERROR,
    } satisfies ModifyWorkbookResult);
  }
  if (normalizedInput.stepId !== null && !step) {
    const available = ctx.planState.steps.map((s) => s.id).join(", ");
    return JSON.stringify({
      ok: false,
      applied: false,
      stepId: normalizedInput.stepId,
      error: `Unknown stepId: "${normalizedInput.stepId}". Available: ${available}`,
    } satisfies ModifyWorkbookResult);
  }

  const targetSheet =
    (normalizedInput.targetSheet ??
      step?.planStep.targetSheet ??
      ctx.session.sessionLockSheet) ||
    undefined;
  const targetedInput: ModifyWorkbookInput = {
    ...normalizedInput,
    modifications: materializeTargetSheet(
      normalizedInput.modifications,
      targetSheet,
    ),
  };
  const worksheetWorkloadOutputScopeError =
    findWorksheetWorkloadOutputScopeError(ctx, targetedInput);
  if (worksheetWorkloadOutputScopeError) {
    return JSON.stringify({
      ok: false,
      applied: false,
      stepId: targetedInput.stepId,
      error: worksheetWorkloadOutputScopeError,
    } satisfies ModifyWorkbookResult);
  }

  const preparedCitations = prepareCitationWrites(
    targetedInput.modifications,
    ctx.accumulators.citationNoteIndex ?? {},
  );
  if (preparedCitations.issues.length > 0) {
    console.warn(
      "[Excel] Skipping invalid generated citation Notes:",
      preparedCitations.issues,
    );
  }

  const sameApplyOverlap = findSameApplyCitedTargetOverlap(
    normalizedInput.modifications,
    preparedCitations.targets,
  );
  let citationTargets = preparedCitations.targets;
  let citationModifications = preparedCitations.modifications;
  if (sameApplyOverlap) {
    console.warn(`[Excel] Skipping ambiguous generated citation Notes: ${sameApplyOverlap}`);
    citationTargets = [];
    citationModifications = stripGeneratedCitationAnnotations(
      preparedCitations.modifications,
    );
  }

  const provenanceIssue = findUncitedCitationOverwrite(
    ctx,
    citationModifications,
    citationTargets,
  );
  if (provenanceIssue) {
    if (!provenanceIssue.allowBestEffortValueWrite) {
      return JSON.stringify({
        ok: false,
        applied: false,
        stepId: normalizedInput.stepId,
        error: provenanceIssue.error,
      } satisfies ModifyWorkbookResult);
    }
    console.warn(
      `[Excel] Citation provenance diagnostic: ${provenanceIssue.error}`,
    );
  }

  const preflight = await preflightCitationNotes(
    ctx,
    citationModifications,
    citationTargets,
  );

  const input: ModifyWorkbookInput = {
    ...targetedInput,
    modifications: preflight.modifications,
  };

  const protectedOverwrite = ctx.session.allowFdsFormulaOverwriteWithStaticValues
    ? undefined
    : findProtectedFdsFormulaOverwrite(ctx, input.modifications);
  if (protectedOverwrite) {
    return JSON.stringify({
      ok: false,
      applied: false,
      stepId: input.stepId,
      error:
        `Refusing to replace sourced FactSet formulas in ` +
        `${protectedOverwrite.sheetName}!${protectedOverwrite.protectedAddress} ` +
        `with unsourced static content at ${protectedOverwrite.writeAddress}. ` +
        "Keep or correct the sourced formulas, or leave the cells blank.",
    } satisfies ModifyWorkbookResult);
  }

  const reason = step
    ? `executor modify_workbook step ${step.planStep.step}`
    : "executor modify_workbook";
  const effectiveModifications = ctx.factSetFormatRegistry
    ? normalizeFactSetFormatsForModification(
        input.modifications,
        ctx.factSetFormatRegistry,
        ctx.session.pinnedWorkbookName,
      )
    : input.modifications;
  const effectiveInput: ModifyWorkbookInput = {
    ...input,
    modifications: effectiveModifications,
  };

  ctx.accumulators.workbookEditAttempted = true;
  ctx.factSetFormatRegistry?.rememberAttemptedModifications(
    effectiveModifications,
    ctx.session.pinnedWorkbookName,
  );

  try {
    const applyResult = await ctx.applyWorkbookModifications({
      modifications: effectiveModifications,
      targetSheet,
      reason,
      pinnedWorkbookName: ctx.session.pinnedWorkbookName,
      // Already normalized above with the run-scoped `factSetFormatRegistry`
      // (accumulated provenance across steps). Tell the host apply path not
      // to re-normalize with a fresh, empty registry — that pass can't see
      // this run's history and would just redundantly re-scan the payload.
      skipFactSetFormatNormalization: Boolean(ctx.factSetFormatRegistry),
    });

    recordApplyResult(ctx, effectiveInput, step, applyResult);
    if ((applyResult.citationNoteFailures?.length ?? 0) > 0) {
      ctx.session.citationNoteAttachmentDisabled = true;
    }
    if (applyResult.success) {
      retireExplicitlyChangedCitationNotes(
        ctx,
        input.modifications,
        preflight.targets,
      );
    }

    const sourceNoteAudit =
      applyResult.success &&
        preflight.targets.length > 0 &&
        !applyResult.retryReason
        ? await auditCitationNotes(ctx, preflight.targets)
        : undefined;
    if (sourceNoteAudit) {
      recordSourceNoteAudit(ctx, preflight.targets, sourceNoteAudit);
      if (!sourceNoteAudit.clean) {
        ctx.session.citationNoteAttachmentDisabled = true;
      }
    }

    return JSON.stringify({
      ok: true,
      applied: applyResult.success,
      stepId: input.stepId,
      message: applyResult.message,
      operations: applyResult.operations,
      editedCells: applyResult.editedCells,
      skippedActions: applyResult.skippedActions,
      failedActions: applyResult.failedActions,
      retryReason: applyResult.retryReason,
      hostDispatchAttempted: applyResult.hostDispatchAttempted,
      deferredRefreshAll: applyResult.deferredRefreshAll,
      deferredRecalcMode: applyResult.deferredRecalcMode,
      ...(sourceNoteAudit?.clean ? { sourceNoteAudit } : {}),
    } satisfies ModifyWorkbookResult);
  } catch (err) {
    if (isAbortError(err)) {
      throw err;
    }
    const message = err instanceof Error ? err.message : String(err);
    recordThrownApplyFailure(ctx, effectiveInput, step, message);
    return JSON.stringify({
      ok: true,
      applied: false,
      stepId: input.stepId,
      error: message,
      ambiguousWrite: true,
      failedActions: ["workbook apply acknowledgement"],
    } satisfies ModifyWorkbookResult);
  }
}

function materializeTargetSheet(
  modifications: WorkbookModification,
  targetSheet: string | undefined,
): WorkbookModification {
  const resolvedSheet = targetSheet?.trim();
  if (!resolvedSheet || !modifications.worksheets?.length) {
    return modifications;
  }
  let changed = false;
  const worksheets = modifications.worksheets.map((worksheet) => {
    if (
      typeof worksheet.sheetName === "string" &&
      worksheet.sheetName.trim().length > 0
    ) {
      return worksheet;
    }
    changed = true;
    return { ...worksheet, sheetName: resolvedSheet };
  });
  return changed ? { ...modifications, worksheets } : modifications;
}

function removeRedundantTtfQueriesFromResolvedFormulas(
  modifications: WorkbookModification,
): void {
  for (const worksheet of modifications.worksheets ?? []) {
    for (const cell of worksheet.cells ?? []) {
      if (
        cell.ttfQuery &&
        (isFdsBoundFormula(cell.formula) || isFdsBoundFormula(cell.value))
      ) {
        delete cell.ttfQuery;
      }
    }
  }
}

export async function executeSetRangeValuesTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = normaliseSetRangeValuesInput(rawInput);
  const error = validateRangeBase(input) || validateValueMatrix(input.values);
  if (error) return granularValidationError(input.stepId, error);
  const shapeIssue = compareRangeAndMatrixShape(
    input.address,
    input.values,
    "values",
    "set_range_values",
  );
  if (shapeIssue) {
    return rangeMatrixShapeError(input.stepId, shapeIssue, "set_range_values");
  }

  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{
        sheetName: input.sheetName,
        ranges: [{
          address: input.address,
          action: "fill",
          values: input.values,
          ...(input.citationIdsMatrix !== undefined
            ? { citationIdsMatrix: input.citationIdsMatrix }
            : {}),
        }],
      }],
      message: `Set values in ${input.sheetName}!${input.address}`,
    } satisfies WorkbookModification,
  });
}

export async function executeSetRangeFormulasTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = normaliseSetRangeFormulasInput(rawInput);
  const error = validateRangeBase(input) || validateFormulaMatrix(input.formulas);
  if (error) return granularValidationError(input.stepId, error);
  const shapeIssue = compareRangeAndMatrixShape(
    input.address,
    input.formulas,
    "formulas",
    "set_range_formulas",
  );
  if (shapeIssue) {
    return rangeMatrixShapeError(input.stepId, shapeIssue, "set_range_formulas");
  }

  const result = await applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{
        sheetName: input.sheetName,
        ranges: [{
          address: input.address,
          action: "fill",
          formulas: input.formulas,
        }],
      }],
      message: `Set formulas in ${input.sheetName}!${input.address}`,
    } satisfies WorkbookModification,
  });
  if (isDiagEnabled()) {
    try {
      const readback = await readRangeContent(
        input.sheetName,
        input.address,
        { includeFormulas: true, includeFormats: false },
        ctx.session.pinnedWorkbookName,
      );
      console.log("[Finster][set_range_formulas readback]", {
        stepId: input.stepId,
        sheetName: input.sheetName,
        address: input.address,
        requestedFormulas: input.formulas,
        toolResult: safeJsonParse(result),
        readback: {
          address: readback.range?.address,
          rowCount: readback.range?.rowCount,
          columnCount: readback.range?.columnCount,
          formulasAvailable: Boolean(readback.range?.formulas),
          formulas: readback.range?.formulas,
          values: readback.range?.values,
        },
      });
    } catch (err) {
      console.warn("[Finster][set_range_formulas readback failed]", {
        stepId: input.stepId,
        sheetName: input.sheetName,
        address: input.address,
        requestedFormulas: input.formulas,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
  return result;
}

export async function executeFormatRangeTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const rawRecord = normaliseRecord(rawInput);
  const input = normaliseFormatRangeInput(rawInput);
  const error =
    validateRangeBase(input) ||
    validateNonEmptyObject(input.format, "format") ||
    validateFocusedFormat(rawRecord.format);
  if (error) return granularValidationError(input.stepId, error);

  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{
        sheetName: input.sheetName,
        ranges: [{
          address: input.address,
          action: "format",
          format: input.format,
        }],
      }],
      message: `Formatted ${input.sheetName}!${input.address}`,
    } satisfies WorkbookModification,
  });
}

export async function executeCreateSheetTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = normaliseCreateSheetInput(rawInput);
  if (!input.name) return granularValidationError(input.stepId, "Missing required parameter: name");

  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: null,
    modifications: {
      sheets: [
        { action: "create", name: input.name },
        ...(input.activate ? [{ action: "activate" as const, name: input.name }] : []),
      ],
      message: `Created worksheet ${input.name}`,
    } satisfies WorkbookModification,
  });
}

export async function executeRenameSheetTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = normaliseRenameSheetInput(rawInput);
  if (!input.name) return granularValidationError(input.stepId, "Missing required parameter: name");
  if (!input.newName) return granularValidationError(input.stepId, "Missing required parameter: newName");

  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.name,
    modifications: {
      sheets: [{ action: "rename", name: input.name, newName: input.newName }],
      message: `Renamed worksheet ${input.name} to ${input.newName}`,
    } satisfies WorkbookModification,
  });
}

export async function executeActivateSheetTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const input = normaliseActivateSheetInput(rawInput);
  if (!input.name) return granularValidationError(input.stepId, "Missing required parameter: name");

  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.name,
    modifications: {
      sheets: [{ action: "activate", name: input.name }],
      message: `Activated worksheet ${input.name}`,
    } satisfies WorkbookModification,
  });
}

export async function executeClearRangeTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const r = normaliseRecord(rawInput);
  const input = {
    stepId: normaliseStepId(r),
    sheetName: normaliseStringField(r, "sheetName"),
    address: normaliseStringField(r, "address"),
  };
  const error = validateRangeBase(input);
  if (error) return granularValidationError(input.stepId, error);
  const clearType = normaliseStringField(r, "clearType") as
    NonNullable<RangeModification["clear"]>["clearType"];
  if (!["Contents", "Formats", "Hyperlinks", "Outline", "All"].includes(clearType ?? "")) {
    return granularValidationError(input.stepId, "Invalid clearType");
  }
  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{
        sheetName: input.sheetName,
        ranges: [{
          address: input.address,
          action: "clear",
          clear: { clearType },
        }],
      }],
      message: `Cleared ${input.sheetName}!${input.address}`,
    },
  });
}

export async function executeEditRangeStructureTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const r = normaliseRecord(rawInput);
  const input = {
    stepId: normaliseStepId(r),
    sheetName: normaliseStringField(r, "sheetName"),
    address: normaliseStringField(r, "address"),
  };
  const error = validateRangeBase(input);
  if (error) return granularValidationError(input.stepId, error);
  const operation = normaliseStringField(r, "operation");
  const destination = normaliseOptionalString(r.destinationAddress);
  const destinationSheetName = normaliseOptionalString(r.destinationSheetName);
  const shiftDirection = normaliseOptionalString(r.shiftDirection);
  const copyOrigin = normaliseOptionalString(r.copyOrigin);
  let modification: RangeModification;
  if (operation === "move" || operation === "copy") {
    if (!destination) {
      return granularValidationError(
        input.stepId,
        "Missing required parameter for move/copy: destinationAddress",
      );
    }
    if (destination.includes("!") || !parseSingleCellAddress(destination)) {
      return granularValidationError(
        input.stepId,
        `Invalid single-cell destinationAddress: "${destination}"`,
      );
    }
    modification = {
      address: input.address,
      action: "moveRange",
      moveRange: {
        destination,
        preserveSource: operation === "copy",
        sourceSheetName: input.sheetName,
        ...(destinationSheetName ? { destinationSheetName } : {}),
      },
    };
  } else if (operation === "insert") {
    if (shiftDirection !== "right" && shiftDirection !== "down") {
      return granularValidationError(
        input.stepId,
        "insert requires shiftDirection right or down",
      );
    }
    if (copyOrigin !== null && copyOrigin !== "leftOrAbove" && copyOrigin !== "rightOrBelow") {
      return granularValidationError(input.stepId, "Invalid copyOrigin");
    }
    const normalizedCopyOrigin =
      copyOrigin === "leftOrAbove" || copyOrigin === "rightOrBelow"
        ? copyOrigin
        : undefined;
    modification = {
      address: input.address,
      action: "insertRange",
      insertRange: {
        shiftDirection,
        ...(normalizedCopyOrigin ? { copyOrigin: normalizedCopyOrigin } : {}),
      },
    };
  } else if (operation === "delete") {
    if (shiftDirection !== "left" && shiftDirection !== "up") {
      return granularValidationError(
        input.stepId,
        "delete requires shiftDirection left or up",
      );
    }
    modification = {
      address: input.address,
      action: "deleteRange",
      deleteRange: { shiftDirection },
    };
  } else {
    return granularValidationError(input.stepId, `Unsupported operation: ${operation || "(missing)"}`);
  }
  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{ sheetName: input.sheetName, ranges: [modification] }],
      message: `${operation} range ${input.sheetName}!${input.address}`,
    },
  });
}

export async function executeLayoutRangeTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const r = normaliseRecord(rawInput);
  const input = {
    stepId: normaliseStepId(r),
    sheetName: normaliseStringField(r, "sheetName"),
    address: normaliseStringField(r, "address"),
  };
  if (!input.sheetName) {
    return granularValidationError(input.stepId, "Missing required parameter: sheetName");
  }
  if (!input.address || input.address.includes("!")) {
    return granularValidationError(input.stepId, "address must be an unqualified local range");
  }
  const operation = normaliseStringField(r, "operation");
  const size = typeof r.size === "number" && Number.isFinite(r.size) ? r.size : null;
  const mapping: Record<string, RangeModification["action"]> = {
    autofit_columns: "autofit",
    autofit_rows: "autofit",
    set_column_width: "resize",
    set_row_height: "resize",
    hide_columns: "hideColumns",
    show_columns: "showColumns",
    hide_rows: "hideRows",
    show_rows: "showRows",
  };
  const action = mapping[operation];
  if (!action) {
    return granularValidationError(input.stepId, `Unsupported operation: ${operation || "(missing)"}`);
  }
  const isCellRange = parseCellRangeAddress(input.address) !== undefined;
  const isColumnAxis = isValidWholeColumnRange(input.address);
  const isRowAxis = isValidWholeRowRange(input.address);
  if (!isCellRange && !isColumnAxis && !isRowAxis) {
    return granularValidationError(
      input.stepId,
      `Invalid address: "${input.address}". Expected a bounded A1 cell, rectangular, row, or column range`,
    );
  }
  const isColumnOperation = operation.endsWith("_columns") || operation === "set_column_width";
  const isRowOperation = operation.endsWith("_rows") || operation === "set_row_height";
  if ((isColumnOperation && isRowAxis) || (isRowOperation && isColumnAxis)) {
    return granularValidationError(
      input.stepId,
      `${isColumnOperation ? "Column" : "Row"} layout requires a cell range or matching axis range`,
    );
  }
  if (
    (operation === "hide_columns" || operation === "show_columns") &&
    !isColumnAxis
  ) {
    return granularValidationError(
      input.stepId,
      "Column visibility requires a column-shaped address such as A:C",
    );
  }
  if (
    (operation === "hide_rows" || operation === "show_rows") &&
    !isRowAxis
  ) {
    return granularValidationError(
      input.stepId,
      "Row visibility requires a row-shaped address such as 5:10",
    );
  }
  if ((operation === "set_column_width" || operation === "set_row_height") && (size === null || size <= 0)) {
    return granularValidationError(input.stepId, "Explicit sizing requires a positive size");
  }
  const modification: RangeModification = {
    address: input.address,
    action,
    ...(operation === "autofit_columns" ? { fitColumns: true } : {}),
    ...(operation === "autofit_rows" ? { fitRows: true } : {}),
    ...(operation === "set_column_width" ? { width: size! } : {}),
    ...(operation === "set_row_height" ? { height: size! } : {}),
  };
  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{ sheetName: input.sheetName, ranges: [modification] }],
      message: `${operation} for ${input.sheetName}!${input.address}`,
    },
  });
}

export async function executeSortRangeTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const r = normaliseRecord(rawInput);
  const input = {
    stepId: normaliseStepId(r),
    sheetName: normaliseStringField(r, "sheetName"),
    address: normaliseStringField(r, "address"),
  };
  const error = validateRangeBase(input);
  if (error) return granularValidationError(input.stepId, error);
  if (!Array.isArray(r.sortKeys) || r.sortKeys.length < 1 || r.sortKeys.length > 3) {
    return granularValidationError(input.stepId, "sortKeys must contain one to three keys");
  }
  const sortRange = parseCellRangeAddress(input.address)!;
  const sortKeys = r.sortKeys.map((rawKey) => {
    const key = normaliseRecord(rawKey);
    return compactNullableRecord({
      key: normaliseStringField(key, "key"),
      order: key.order,
      dataOption: key.dataOption,
    });
  }) as NonNullable<RangeModification["sort"]>["sortKeys"];
  if (sortKeys.some((key) => !key.key)) {
    return granularValidationError(input.stepId, "Every sort key requires key");
  }
  for (const key of sortKeys) {
    const parsedKey = parseSingleCellAddress(key.key);
    if (
      !parsedKey ||
      key.key.includes("!") ||
      parsedKey.column < sortRange.startColumn ||
      parsedKey.column > sortRange.endColumn ||
      parsedKey.row < sortRange.startRow ||
      parsedKey.row > sortRange.endRow
    ) {
      return granularValidationError(
        input.stepId,
        `Invalid sort key "${key.key}": expected an unqualified cell inside ${input.address}`,
      );
    }
    if (
      key.order !== undefined &&
      key.order !== "Ascending" &&
      key.order !== "Descending"
    ) {
      return granularValidationError(input.stepId, `Invalid sort key order: ${String(key.order)}`);
    }
    if (
      key.dataOption !== undefined &&
      key.dataOption !== "Normal" &&
      key.dataOption !== "TextAsNumbers"
    ) {
      return granularValidationError(
        input.stepId,
        `Invalid sort key dataOption: ${String(key.dataOption)}`,
      );
    }
  }
  if (r.header != null && !["Guess", "Yes", "No"].includes(String(r.header))) {
    return granularValidationError(input.stepId, `Invalid sort header: ${String(r.header)}`);
  }
  if (r.matchCase != null && typeof r.matchCase !== "boolean") {
    return granularValidationError(input.stepId, "sort matchCase must be boolean or null");
  }
  if (
    r.orientation != null &&
    !["TopToBottom", "LeftToRight"].includes(String(r.orientation))
  ) {
    return granularValidationError(
      input.stepId,
      `Invalid sort orientation: ${String(r.orientation)}`,
    );
  }
  if (r.sortMethod != null && !["PinYin", "Stroke"].includes(String(r.sortMethod))) {
    return granularValidationError(
      input.stepId,
      `Invalid sort method: ${String(r.sortMethod)}`,
    );
  }
  const options = compactNullableRecord({
    header: r.header,
    matchCase: r.matchCase,
    orientation: r.orientation,
    sortMethod: r.sortMethod,
  }) as NonNullable<RangeModification["sort"]>["options"];
  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{
        sheetName: input.sheetName,
        ranges: [{
          address: input.address,
          action: "sort",
          sort: { sortKeys, ...(Object.keys(options ?? {}).length ? { options } : {}) },
        }],
      }],
      message: `Sorted ${input.sheetName}!${input.address}`,
    },
  });
}

export async function executeManageWorksheetTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const r = normaliseRecord(rawInput);
  const stepId = normaliseStepId(r);
  const operation = normaliseStringField(r, "operation");
  const name = normaliseStringField(r, "name");
  if (!name) return granularValidationError(stepId, "Missing required parameter: name");
  let sheet: SheetModification;
  if (operation === "create") {
    sheet = { action: "create", name };
  } else if (operation === "rename") {
    const newName = normaliseOptionalString(r.newName);
    if (!newName) return granularValidationError(stepId, "rename requires newName");
    sheet = { action: "rename", name, newName };
  } else if (operation === "delete" || operation === "activate") {
    sheet = { action: operation, name };
  } else if (operation === "move") {
    if (typeof r.newIndex !== "number" || !Number.isInteger(r.newIndex) || r.newIndex < 0) {
      return granularValidationError(stepId, "move requires a non-negative integer newIndex");
    }
    sheet = {
      action: "move",
      name,
      move: {
        newIndex: r.newIndex,
        ...(normaliseOptionalString(r.sourceWorkbookName)
          ? { sourceWorkbookName: normaliseOptionalString(r.sourceWorkbookName)! }
          : {}),
        ...(normaliseOptionalString(r.targetWorkbookName)
          ? { targetWorkbookName: normaliseOptionalString(r.targetWorkbookName)! }
          : {}),
        selectSheet: r.activate === true,
      },
    };
  } else {
    return granularValidationError(stepId, `Unsupported operation: ${operation || "(missing)"}`);
  }
  const sheets = operation === "create" && r.activate === true
    ? [sheet, { action: "activate" as const, name }]
    : [sheet];
  return applyValidatedWorkbookModification(ctx, {
    stepId,
    targetSheet: operation === "create" ? null : name,
    modifications: { sheets, message: `${operation} worksheet ${name}` },
  });
}

export async function executeCopyWorksheetTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const r = normaliseRecord(rawInput);
  const stepId = normaliseStepId(r);
  const sourceSheetName = normaliseStringField(r, "sourceSheetName");
  if (!sourceSheetName) {
    return granularValidationError(stepId, "Missing required parameter: sourceSheetName");
  }
  const location = normaliseOptionalString(r.location);
  if (location !== null && location !== "before" && location !== "after") {
    return granularValidationError(stepId, "location must be before, after, or null");
  }
  const operation: WorkbookOperation = {
    action: "copyWorksheet",
    copyWorksheet: {
      sourceSheetName,
      ...(location === "before" || location === "after" ? { location } : {}),
    },
  };
  return applyValidatedWorkbookModification(ctx, {
    stepId,
    targetSheet: sourceSheetName,
    modifications: {
      workbookOperations: [operation],
      message: `Copied worksheet ${sourceSheetName}`,
    },
  });
}

export async function executeManageNamedRangeTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const r = normaliseRecord(rawInput);
  const stepId = normaliseStepId(r);
  const operation = normaliseStringField(r, "operation");
  const name = normaliseStringField(r, "name");
  if (!name) return granularValidationError(stepId, "Missing required parameter: name");
  let workbookOperation: WorkbookOperation;
  if (operation === "create_or_update") {
    const refersTo = normaliseOptionalString(r.refersTo);
    if (!refersTo) return granularValidationError(stepId, "create_or_update requires refersTo");
    workbookOperation = {
      action: "addNamedObject",
      addNamedObject: {
        name,
        refersTo,
        ...(normaliseOptionalString(r.workbookName)
          ? { workbookName: normaliseOptionalString(r.workbookName)! }
          : {}),
        ...(typeof r.scope === "number" ? { scope: r.scope } : {}),
        ...(typeof r.visible === "boolean" ? { isVisible: r.visible } : {}),
        ...(normaliseOptionalString(r.comment) ? { comment: normaliseOptionalString(r.comment)! } : {}),
      },
    };
  } else if (operation === "delete") {
    workbookOperation = {
      action: "removeNamedObject",
      removeNamedObject: {
        name,
        ...(normaliseOptionalString(r.workbookName)
          ? { workbookName: normaliseOptionalString(r.workbookName)! }
          : {}),
        ...(typeof r.scope === "number" ? { scope: r.scope } : {}),
      },
    };
  } else {
    return granularValidationError(stepId, `Unsupported operation: ${operation || "(missing)"}`);
  }
  return applyValidatedWorkbookModification(ctx, {
    stepId,
    targetSheet: null,
    modifications: {
      workbookOperations: [workbookOperation],
      message: `${operation} named range ${name}`,
    },
  });
}

export async function executeInsertCompanyLogoTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const r = normaliseRecord(rawInput);
  const input = {
    stepId: normaliseStepId(r),
    sheetName: normaliseStringField(r, "sheetName"),
    address: normaliseStringField(r, "address"),
  };
  const error = validateRangeBase(input);
  if (error) return granularValidationError(input.stepId, error);
  if (!parseSingleCellAddress(input.address)) {
    return granularValidationError(
      input.stepId,
      "Logo address must be a single cell such as A1",
    );
  }
  const companyName = normaliseStringField(r, "companyName");
  if (!companyName) {
    return granularValidationError(input.stepId, "Missing required parameter: companyName");
  }
  return applyValidatedWorkbookModification(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [{
        sheetName: input.sheetName,
        logos: [{
          companyName,
          address: input.address,
          ...(typeof r.width === "number" ? { width: r.width } : {}),
          ...(typeof r.height === "number" ? { height: r.height } : {}),
        }],
      }],
      message: `Inserted ${companyName} logo at ${input.sheetName}!${input.address}`,
    },
  });
}

/**
 * Final live-read guard for percent formats whose display scale conflicts
 * with tracked FactSet return semantics.
 */
export async function executeFactSetFormatSafetyPass(
  ctx: ExecutorToolRuntimeContext,
  deps: { readRange?: typeof readRangeContent } = {},
): Promise<{ corrected: number; error?: string }> {
  const registry = ctx.factSetFormatRegistry;
  if (!registry) return { corrected: 0 };
  const trackedCells = registry
    .listSafetyCells(ctx.session.pinnedWorkbookName)
    .filter(
      (cell) =>
        cell.hint.semantics === "percentage_points" ||
        cell.hint.semantics === "decimal_fraction",
    );
  if (trackedCells.length === 0) return { corrected: 0 };

  const overridesBySheet = new Map<
    string,
    Array<{
      address: string;
      formula: string;
      format: string;
      hint: FactSetFormatHint;
    }>
  >();
  const errors: string[] = [];
  const readRange = deps.readRange ?? readRangeContent;
  const initialRead = await readTrackedFactSetCells(
    trackedCells,
    readRange,
    ctx.session.pinnedWorkbookName,
  );
  errors.push(...initialRead.errors);
  for (const cell of trackedCells) {
    const liveCell = initialRead.cells.get(trackedFactSetCellReadKey(cell));
    if (!liveCell) {
      errors.push(
        `${cell.sheetName}!${cell.address}: live cell metadata unavailable`,
      );
      continue;
    }
    const formula = liveCell.formula;
    if (typeof formula !== "string") {
      errors.push(
        `${cell.sheetName}!${cell.address}: formula metadata unavailable`,
      );
      continue;
    }
    const liveHint = registry.resolveFormula(formula);
    if (
      liveHint?.semantics !== "percentage_points" &&
      liveHint?.semantics !== "decimal_fraction"
    ) {
      continue;
    }
    if (
      !sameFactSetFormulaIdentity(cell.formula, formula) &&
      liveHint.source !== "data_item"
    ) {
      continue;
    }

    const numberFormat = liveCell.numberFormat;
    if (typeof numberFormat !== "string") {
      errors.push(
        `${cell.sheetName}!${cell.address}: number format metadata unavailable`,
      );
      continue;
    }
    const normalized = normalizeFactSetNumberFormat(
      numberFormat,
      liveHint.semantics,
    );
    if (!normalized.changed) continue;
    const sheetOverrides = overridesBySheet.get(cell.sheetName) ?? [];
    sheetOverrides.push({
      address: cell.address,
      formula,
      format: normalized.format,
      hint: liveHint,
    });
    overridesBySheet.set(cell.sheetName, sheetOverrides);
  }

  const worksheets = Array.from(overridesBySheet.entries()).map(
    ([sheetName, overrides]) => ({
      sheetName,
      ranges: overrides.map((override) => ({
        address: override.address,
        action: "format" as const,
        format: { numberFormat: { format: override.format } },
      })),
    }),
  );
  const corrected = worksheets.reduce(
    (count, worksheet) => count + (worksheet.ranges?.length ?? 0),
    0,
  );
  if (corrected === 0) {
    return {
      corrected: 0,
      ...(errors.length
        ? { error: `Could not verify FactSet formats: ${errors.join("; ")}` }
        : {}),
    };
  }

  try {
    const applyResult = await ctx.applyWorkbookModifications({
      modifications: {
        worksheets,
        message: "Normalized FactSet percent-scale number formats",
      },
      reason: "normalize FactSet percent-scale formats",
      pinnedWorkbookName: ctx.session.pinnedWorkbookName,
    });
    if (
      !applyResult.success ||
      (applyResult.failedActions?.length ?? 0) > 0 ||
      (applyResult.skippedActions?.length ?? 0) > 0
    ) {
      return {
        corrected: 0,
        error:
          applyResult.message ||
          "FactSet percent-scale format normalization did not apply",
      };
    }
    ctx.accumulators.editedCells.push(...(applyResult.editedCells ?? []));
    const correctionCells = trackedCells.filter((cell) =>
      (overridesBySheet.get(cell.sheetName) ?? []).some(
        (override) => override.address === cell.address,
      ),
    );
    const verification = await readTrackedFactSetCells(
      correctionCells,
      readRange,
      ctx.session.pinnedWorkbookName,
    );
    errors.push(...verification.errors);
    let verifiedCorrections = 0;
    for (const [sheetName, overrides] of Array.from(
      overridesBySheet.entries(),
    )) {
      for (const override of overrides) {
        const tracked = correctionCells.find(
          (cell) =>
            cell.sheetName === sheetName && cell.address === override.address,
        );
        const liveCell = tracked
          ? verification.cells.get(trackedFactSetCellReadKey(tracked))
          : undefined;
        const liveNumberFormat =
          typeof liveCell?.numberFormat === "string"
            ? liveCell.numberFormat
            : undefined;
        const formatVerified =
          override.hint.semantics === "percentage_points"
            ? liveNumberFormat !== undefined &&
              !hasMultiplyingPercentToken(liveNumberFormat) &&
              hasLiteralPercentToken(liveNumberFormat)
            : override.hint.semantics === "decimal_fraction"
              ? liveNumberFormat !== undefined &&
                hasMultiplyingPercentToken(liveNumberFormat) &&
                !hasLiteralPercentToken(liveNumberFormat)
              : false;
        if (
          !tracked ||
          !liveCell ||
          typeof liveCell.formula !== "string" ||
          !sameFactSetFormulaIdentity(override.formula, liveCell.formula) ||
          !formatVerified
        ) {
          errors.push(
            `${sheetName}!${override.address}: percent-scale correction was not verified`,
          );
          continue;
        }
        verifiedCorrections += 1;
        diag("format", "factset_percent_safety_pass_corrected", {
          sheetName,
          address: override.address,
          dataItem: override.hint.dataItem,
          correctedFormat: override.format,
        });
      }
    }
    return {
      corrected: verifiedCorrections,
      ...(errors.length
        ? {
            error: `Some FactSet formats could not be verified: ${errors.join("; ")}`,
          }
        : {}),
    };
  } catch (error) {
    return {
      corrected: 0,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

interface FactSetSafetyReadCell {
  formula?: unknown;
  numberFormat?: unknown;
}

interface FactSetSafetyReadGroup {
  sheetName: string;
  address: string;
  startRow: number;
  startColumn: number;
  cells: TrackedFactSetCell[];
}

async function readTrackedFactSetCells(
  trackedCells: readonly TrackedFactSetCell[],
  readRange: typeof readRangeContent,
  workbookName?: string,
): Promise<{
  cells: Map<string, FactSetSafetyReadCell>;
  errors: string[];
}> {
  const cells = new Map<string, FactSetSafetyReadCell>();
  const errors: string[] = [];
  for (const group of groupTrackedCellsForRead(trackedCells)) {
    let readback: Awaited<ReturnType<typeof readRangeContent>>;
    try {
      readback = await readRange(
        group.sheetName,
        group.address,
        { includeFormulas: true, includeFormats: true },
        workbookName,
      );
    } catch (error) {
      errors.push(
        `${group.sheetName}!${group.address}: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
      continue;
    }
    for (const cell of group.cells) {
      const parsed = parseCellRange(cell.address);
      if (!parsed) continue;
      const readStartRow =
        readback.range?.startRow ?? group.startRow;
      const readStartColumn =
        readback.range?.startColumn ?? group.startColumn;
      const rowOffset = parsed.startRow - readStartRow;
      const columnOffset = parsed.startColumn - readStartColumn;
      if (rowOffset < 0 || columnOffset < 0) continue;
      cells.set(trackedFactSetCellReadKey(cell), {
        formula: readback.range?.formulas?.[rowOffset]?.[columnOffset],
        numberFormat:
          readback.range?.numberFormats?.[rowOffset]?.[columnOffset],
      });
    }
  }
  return { cells, errors };
}

function groupTrackedCellsForRead(
  trackedCells: readonly TrackedFactSetCell[],
): FactSetSafetyReadGroup[] {
  const bySheet = new Map<
    string,
    Array<{ cell: TrackedFactSetCell; row: number; column: number }>
  >();
  for (const cell of trackedCells) {
    const parsed = parseCellRange(cell.address);
    if (!parsed) continue;
    const entries = bySheet.get(cell.sheetName) ?? [];
    entries.push({
      cell,
      row: parsed.startRow,
      column: parsed.startColumn,
    });
    bySheet.set(cell.sheetName, entries);
  }

  const groups: FactSetSafetyReadGroup[] = [];
  for (const [sheetName, entries] of Array.from(bySheet.entries())) {
    entries.sort((left, right) => left.row - right.row || left.column - right.column);
    let current: typeof entries = [];
    let startRow = 0;
    let endRow = 0;
    let startColumn = 0;
    let endColumn = 0;
    const flush = (): void => {
      if (current.length === 0) return;
      const start = `${excelColumnName(startColumn)}${startRow + 1}`;
      const end = `${excelColumnName(endColumn)}${endRow + 1}`;
      groups.push({
        sheetName,
        address: start === end ? start : `${start}:${end}`,
        startRow,
        startColumn,
        cells: current.map((entry) => entry.cell),
      });
      current = [];
    };
    for (const entry of entries) {
      if (current.length === 0) {
        current = [entry];
        startRow = endRow = entry.row;
        startColumn = endColumn = entry.column;
        continue;
      }
      const nextStartRow = Math.min(startRow, entry.row);
      const nextEndRow = Math.max(endRow, entry.row);
      const nextStartColumn = Math.min(startColumn, entry.column);
      const nextEndColumn = Math.max(endColumn, entry.column);
      const nextArea =
        (nextEndRow - nextStartRow + 1) *
        (nextEndColumn - nextStartColumn + 1);
      if (current.length >= 200 || nextArea > 5_000) {
        flush();
        current = [entry];
        startRow = endRow = entry.row;
        startColumn = endColumn = entry.column;
        continue;
      }
      current.push(entry);
      startRow = nextStartRow;
      endRow = nextEndRow;
      startColumn = nextStartColumn;
      endColumn = nextEndColumn;
    }
    flush();
  }
  return groups;
}

function trackedFactSetCellReadKey(cell: TrackedFactSetCell): string {
  return `${cell.sheetName.trim().toLowerCase()}!${cell.address
    .replace(/\$/g, "")
    .toUpperCase()}`;
}

/**
 * Detects cancellation errors that must propagate to the runner instead of
 * being converted into a model-visible tool failure.
 */
function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === "AbortError") ||
    (err instanceof Error && err.name === "AbortError")
  );
}

interface CitationNotePreflightResult {
  modifications: WorkbookModification;
  targets: CitationNoteTarget[];
}

function stripGeneratedCitationAnnotations(
  modifications: WorkbookModification,
  allowedTargets?: readonly CitationNoteTarget[],
): WorkbookModification {
  const allowed = allowedTargets
    ? new Set(
        allowedTargets.map((target) =>
          citationCellKey(target.sheetName, target.address).toLowerCase()
        ),
      )
    : undefined;
  const annotations = (modifications.annotations ?? []).filter((annotation) => {
    if (
      annotation.kind !== "note" ||
      annotation.action !== "set" ||
      annotation.origin !== "citation"
    ) {
      return true;
    }
    if (!allowed) return false;
    return allowed.has(
      citationCellKey(annotation.sheetName, annotation.address).toLowerCase(),
    );
  });
  return {
    ...modifications,
    ...(annotations.length > 0 ? { annotations } : { annotations: undefined }),
  };
}

/**
 * Preserve user-authored Notes before a cited write. Generated source Notes
 * are replaceable; any other non-empty Note is a hard conflict and prevents
 * both the value write and its source Note from reaching the host.
 */
async function preflightCitationNotes(
  ctx: ExecutorToolRuntimeContext,
  modifications: WorkbookModification,
  targets: readonly CitationNoteTarget[],
): Promise<CitationNotePreflightResult> {
  if (
    targets.length === 0 ||
    ctx.session.citationNoteAttachmentDisabled ||
    ctx.session.citationNoteCapability === "unsupported"
  ) {
    return {
      modifications: stripGeneratedCitationAnnotations(modifications),
      targets: [],
    };
  }

  const replaceExistingByCell = new Map<string, boolean>();
  const eligibleTargets: CitationNoteTarget[] = [];
  for (const target of targets) {
    const cell = citationCellKey(target.sheetName, target.address);
    let existing: string;
    try {
      existing = await getNote({
        address: target.address,
        referenceType: "absolute",
        worksheetName: target.sheetName,
        ...(ctx.session.pinnedWorkbookName
          ? { workbookName: ctx.session.pinnedWorkbookName }
          : {}),
      });
    } catch (error) {
      if (isAbortError(error)) throw error;
      ctx.session.citationNoteAttachmentDisabled = true;
      console.warn(
        `[Excel] Generated citation Note preflight unavailable for ${cell}; ` +
          "skipping citation Notes for this run.",
        error,
      );
      return {
        modifications: stripGeneratedCitationAnnotations(modifications),
        targets: [],
      };
    }

    if (existing.trim() && !isGeneratedSourceNote(existing)) {
      console.warn(
        `[Excel] Preserving user-authored Note on ${cell}; generated citation Note skipped.`,
      );
      continue;
    }
    replaceExistingByCell.set(cell.toLowerCase(), existing.length > 0);
    eligibleTargets.push(target);
    if (ctx.session.citationNoteCapability === "unknown") {
      ctx.session.citationNoteCapability = "supported";
    }
  }

  const eligibleModifications = stripGeneratedCitationAnnotations(
    modifications,
    eligibleTargets,
  );
  const annotations = (eligibleModifications.annotations ?? []).map((annotation) => {
    if (annotation.kind !== "note" || annotation.action !== "set") {
      return annotation;
    }
    const cell = citationCellKey(
      annotation.sheetName,
      annotation.address,
    ).toLowerCase();
    const replaceExisting = replaceExistingByCell.get(cell);
    return replaceExisting === undefined
      ? annotation
      : { ...annotation, replaceExisting };
  });

  return {
    modifications: {
      ...eligibleModifications,
      ...(annotations.length > 0 ? { annotations } : { annotations: undefined }),
    },
    targets: eligibleTargets,
  };
}

async function auditCitationNotes(
  ctx: ExecutorToolRuntimeContext,
  targets: readonly CitationNoteTarget[],
): Promise<SourceNoteAudit> {
  const actualNotes = new Map<string, string | undefined>();
  for (const target of targets) {
    const cell = citationCellKey(target.sheetName, target.address);
    try {
      actualNotes.set(cell, await getNote({
        address: target.address,
        referenceType: "absolute",
        worksheetName: target.sheetName,
        ...(ctx.session.pinnedWorkbookName
          ? { workbookName: ctx.session.pinnedWorkbookName }
          : {}),
      }));
    } catch (error) {
      if (isAbortError(error)) throw error;
      actualNotes.set(cell, undefined);
    }
  }
  return buildSourceNoteAudit(targets, actualNotes);
}

function recordSourceNoteAudit(
  ctx: ExecutorToolRuntimeContext,
  targets: readonly CitationNoteTarget[],
  audit: SourceNoteAudit,
): void {
  ctx.accumulators.sourceNoteAudits ??= [];
  ctx.accumulators.sourceNoteAudits.push(audit);
  ctx.accumulators.citationCellProvenance ??= {};
  const presentCells = new Set(
    audit.presentCells.map((cell) => cell.toLowerCase()),
  );
  for (const target of targets) {
    const cell = citationCellKey(target.sheetName, target.address);
    deleteCitationProvenance(ctx.accumulators.citationCellProvenance, cell);
    if (presentCells.has(cell.toLowerCase())) {
      setCitationProvenance(
        ctx.accumulators.citationCellProvenance,
        cell,
        target.citationIds,
      );
    }
  }
}

function setCitationProvenance(
  provenance: Record<string, string[]>,
  cell: string,
  citationIds: readonly string[],
): void {
  deleteCitationProvenance(provenance, cell);
  provenance[cell] = citationIds.slice();
}

function deleteCitationProvenance(
  provenance: Record<string, string[]>,
  cell: string,
): void {
  const normalized = cell.toLowerCase();
  for (const key of Object.keys(provenance)) {
    if (key.toLowerCase() === normalized) delete provenance[key];
  }
}

function splitCitationCellKey(
  cell: string,
): { sheetName: string; address: string } | undefined {
  const separator = cell.lastIndexOf("!");
  if (separator <= 0 || separator >= cell.length - 1) return undefined;
  return {
    sheetName: cell.slice(0, separator),
    address: cell.slice(separator + 1),
  };
}

function noteAnnotationCells(
  modifications: WorkbookModification,
  excludedTargets: readonly CitationNoteTarget[] = [],
): Set<string> {
  const excluded = new Set(
    excludedTargets.map((target) =>
      citationCellKey(target.sheetName, target.address).toLowerCase()
    ),
  );
  const cells = new Set<string>();
  for (const annotation of modifications.annotations ?? []) {
    if (annotation.kind !== "note") continue;
    const addresses = annotation.action === "set"
      ? [annotation.address]
      : annotation.addresses;
    for (const address of addresses) {
      const cell = citationCellKey(
        annotation.sheetName,
        address,
      ).toLowerCase();
      if (!excluded.has(cell)) cells.add(cell);
    }
  }
  return cells;
}

function normalizeQualifiedSheetName(value: string): string {
  const trimmed = value.trim();
  if (
    trimmed.length >= 2 &&
    trimmed.startsWith("'") &&
    trimmed.endsWith("'")
  ) {
    return trimmed.slice(1, -1).split("''").join("'");
  }
  return trimmed;
}

function resolveWorksheetAddress(
  enclosingSheetName: string,
  address: string,
): { sheetName: string; address: string } {
  const separator = address.lastIndexOf("!");
  if (separator < 0) {
    return { sheetName: enclosingSheetName, address: address.trim() };
  }
  return {
    sheetName: normalizeQualifiedSheetName(address.slice(0, separator)),
    address: address.slice(separator + 1).trim(),
  };
}

function isAsciiLetters(value: string): boolean {
  return value.length > 0 && Array.from(value).every((character) => {
    const code = character.charCodeAt(0);
    return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
  });
}

function isAsciiDigits(value: string): boolean {
  return value.length > 0 && Array.from(value).every((character) => {
    const code = character.charCodeAt(0);
    return code >= 48 && code <= 57;
  });
}

function oneBasedColumnNumber(value: string): number | undefined {
  const normalized = value.split("$").join("");
  if (!isAsciiLetters(normalized)) return undefined;
  let column = 0;
  for (const character of normalized.toUpperCase()) {
    column = column * 26 + character.charCodeAt(0) - 64;
  }
  return column >= 1 && column <= 16_384 ? column : undefined;
}

function oneBasedRowNumber(value: string): number | undefined {
  const normalized = value.split("$").join("");
  if (!isAsciiDigits(normalized) || normalized.startsWith("0")) return undefined;
  const row = Number(normalized);
  return Number.isInteger(row) && row >= 1 && row <= 1_048_576
    ? row
    : undefined;
}

function isValidWholeColumnRange(value: string): boolean {
  const parts = value.split(":");
  if (parts.length < 1 || parts.length > 2) return false;
  const start = oneBasedColumnNumber(parts[0]);
  const end = oneBasedColumnNumber(parts[1] ?? parts[0]);
  return start !== undefined && end !== undefined && start <= end;
}

function isValidWholeRowRange(value: string): boolean {
  const parts = value.split(":");
  if (parts.length < 1 || parts.length > 2) return false;
  const start = oneBasedRowNumber(parts[0]);
  const end = oneBasedRowNumber(parts[1] ?? parts[0]);
  return start !== undefined && end !== undefined && start <= end;
}

/** Return whether a cell-shaped, whole-row, or whole-column A1 range contains a cell. */
function addressContainsCell(address: string, cellAddress: string): boolean {
  const cell = parseSingleCellAddress(cellAddress);
  if (!cell) return false;
  const cellRange = parseCellRangeAddress(address);
  if (cellRange) {
    return (
      cell.column >= cellRange.startColumn &&
      cell.column <= cellRange.endColumn &&
      cell.row >= cellRange.startRow &&
      cell.row <= cellRange.endRow
    );
  }

  const colon = address.indexOf(":");
  if (colon < 0 || colon !== address.lastIndexOf(":")) return false;
  const start = address.slice(0, colon).trim();
  const end = address.slice(colon + 1).trim();
  const startColumn = oneBasedColumnNumber(start);
  const endColumn = oneBasedColumnNumber(end);
  if (startColumn !== undefined && endColumn !== undefined) {
    return (
      cell.column >= Math.min(startColumn, endColumn) &&
      cell.column <= Math.max(startColumn, endColumn)
    );
  }
  const startRow = oneBasedRowNumber(start);
  const endRow = oneBasedRowNumber(end);
  return (
    startRow !== undefined &&
    endRow !== undefined &&
    cell.row >= Math.min(startRow, endRow) &&
    cell.row <= Math.max(startRow, endRow)
  );
}

function explicitCitationRetirementCells(
  modifications: WorkbookModification,
  excludedTargets: readonly CitationNoteTarget[],
  provenance: Record<string, string[]>,
): Set<string> {
  const cells = noteAnnotationCells(modifications, excludedTargets);
  for (const cell of Object.keys(provenance)) {
    const parsed = splitCitationCellKey(cell);
    if (!parsed) continue;
    for (const worksheet of modifications.worksheets ?? []) {
      const enclosingSheetName = worksheet.sheetName?.trim() ?? "";
      for (const range of worksheet.ranges ?? []) {
        if (range.action !== "clear") continue;
        const clearType = range.clear?.clearType ?? "All";
        if (clearType !== "All" && clearType !== "Notes") continue;
        const resolved = resolveWorksheetAddress(
          enclosingSheetName,
          range.address,
        );
        if (
          resolved.sheetName.toLowerCase() === parsed.sheetName.toLowerCase() &&
          addressContainsCell(resolved.address, parsed.address)
        ) {
          cells.add(cell.toLowerCase());
        }
      }
    }
  }
  return cells;
}

/**
 * A generated Note audit can only prove Note attachment, not which of several
 * overlapping writes supplied the final value. Require one and only one
 * content-producing operation for every cited target in a modification.
 */
function findSameApplyCitedTargetOverlap(
  modifications: WorkbookModification,
  citedTargets: readonly CitationNoteTarget[],
): string | undefined {
  for (const target of citedTargets) {
    let touchingContentOperations = 0;
    for (const worksheet of modifications.worksheets ?? []) {
      const enclosingSheetName = worksheet.sheetName?.trim() ?? "";
      for (const cell of worksheet.cells ?? []) {
        if (cell.action !== "set" && cell.action !== "clear") continue;
        const resolved = resolveWorksheetAddress(
          enclosingSheetName,
          cell.address,
        );
        if (
          resolved.sheetName.toLowerCase() === target.sheetName.toLowerCase() &&
          addressContainsCell(resolved.address, target.address)
        ) {
          touchingContentOperations += 1;
        }
      }

      for (const range of worksheet.ranges ?? []) {
        const sourceSheetName =
          range.action === "moveRange"
            ? range.moveRange?.sourceSheetName ?? enclosingSheetName
            : enclosingSheetName;
        const resolved = resolveWorksheetAddress(
          sourceSheetName,
          range.address,
        );
        const sameSourceSheet =
          resolved.sheetName.toLowerCase() === target.sheetName.toLowerCase();

        if (
          range.action === "insertRange" ||
          range.action === "deleteRange"
        ) {
          if (sameSourceSheet) {
            return (
              `Cited target ${target.sheetName}!${target.address} cannot share an ` +
              `${range.action} operation in the same workbook apply.`
            );
          }
          continue;
        }

        if (range.action === "moveRange") {
          const destination = resolveWorksheetAddress(
            range.moveRange?.destinationSheetName ?? enclosingSheetName,
            range.moveRange?.destination ?? "",
          );
          if (
            sameSourceSheet ||
            destination.sheetName.toLowerCase() === target.sheetName.toLowerCase()
          ) {
            return (
              `Cited target ${target.sheetName}!${target.address} cannot share a ` +
              "moveRange operation in the same workbook apply."
            );
          }
          continue;
        }

        const containsTarget =
          sameSourceSheet &&
          addressContainsCell(resolved.address, target.address);
        if (!containsTarget) continue;
        if (range.action === "fill" || range.action === "sort") {
          touchingContentOperations += 1;
        } else if (range.action === "clear") {
          const clearType = range.clear?.clearType ?? "All";
          if (clearType === "All" || clearType === "Contents") {
            touchingContentOperations += 1;
          }
        }
      }
    }

    if (touchingContentOperations !== 1) {
      return (
        `Cited target ${target.sheetName}!${target.address} must have exactly one ` +
        `content write in the same apply; found ${touchingContentOperations}.`
      );
    }
  }
  return undefined;
}

/**
 * Prevent a later unsourced value write from leaving a previously generated
 * source Note attached to different content. The agent can carry the exact IDs
 * into the replacement write, or atomically retire the source Note while also
 * replacing/clearing the cited content. Note-only retirement is rejected so it
 * cannot erase verification debt while leaving the sourced value in place.
 */
interface CitationProvenanceIssue {
  error: string;
  allowBestEffortValueWrite: boolean;
}

function findUncitedCitationOverwrite(
  ctx: ExecutorToolRuntimeContext,
  modifications: WorkbookModification,
  citedTargets: readonly CitationNoteTarget[],
): CitationProvenanceIssue | undefined {
  const provenance = ctx.accumulators.citationCellProvenance ?? {};
  if (Object.keys(provenance).length === 0) return undefined;
  let deferredContentWriteIssue: CitationProvenanceIssue | undefined;
  const citedCells = new Set(
    citedTargets.map((target) =>
      citationCellKey(target.sheetName, target.address).toLowerCase()
    ),
  );
  const explicitlyRetiredNotes = explicitCitationRetirementCells(
    modifications,
    citedTargets,
    provenance,
  );

  for (const sheetOperation of modifications.sheets ?? []) {
    if (
      (sheetOperation.action === "delete" ||
        sheetOperation.action === "rename" ||
        sheetOperation.action === "move") &&
      sheetOperation.name &&
      Object.keys(provenance).some((cell) =>
        splitCitationCellKey(cell)?.sheetName.toLowerCase() ===
        sheetOperation.name!.trim().toLowerCase()
      )
    ) {
      return {
        error:
          `Refusing to ${sheetOperation.action} worksheet "${sheetOperation.name}" ` +
          "while it contains run-scoped citation provenance. Remove its source Notes first.",
        allowBestEffortValueWrite: false,
      };
    }
  }

  for (const cell of Object.keys(provenance)) {
    const parsed = splitCitationCellKey(cell);
    if (!parsed) continue;
    const normalizedCell = cell.toLowerCase();
    if (citedCells.has(normalizedCell)) continue;
    let changesContent = false;
    for (const worksheet of modifications.worksheets ?? []) {
      const enclosingSheetName = worksheet.sheetName?.trim() ?? "";
      const cellWrite = (worksheet.cells ?? []).find(
        (candidate) => {
          if (candidate.action !== "set" && candidate.action !== "clear") {
            return false;
          }
          const resolved = resolveWorksheetAddress(
            enclosingSheetName,
            candidate.address,
          );
          return (
            resolved.sheetName.toLowerCase() === parsed.sheetName.toLowerCase() &&
            addressContainsCell(resolved.address, parsed.address)
          );
        },
      );
      if (cellWrite) {
        changesContent = true;
        if (!explicitlyRetiredNotes.has(normalizedCell)) {
          deferredContentWriteIssue ??= {
            error:
              `Refusing an unsourced overwrite of cited cell ${cell}. ` +
              "Carry the exact citation IDs into the replacement write, or explicitly remove/replace its source Note.",
            allowBestEffortValueWrite: true,
          };
        }
      }

      for (const range of worksheet.ranges ?? []) {
        const sourceSheetName =
          range.action === "moveRange"
            ? range.moveRange?.sourceSheetName ?? enclosingSheetName
            : enclosingSheetName;
        const resolved = resolveWorksheetAddress(
          sourceSheetName,
          range.address,
        );
        const sameSourceSheet =
          resolved.sheetName.toLowerCase() === parsed.sheetName.toLowerCase();
        const containsCell =
          sameSourceSheet && addressContainsCell(resolved.address, parsed.address);

        if (range.action === "insertRange" || range.action === "deleteRange") {
          if (sameSourceSheet) {
            return {
              error:
                `Refusing ${range.action} on worksheet "${parsed.sheetName}" while it ` +
                "contains run-scoped citation provenance. Remove its source Notes first.",
              allowBestEffortValueWrite: false,
            };
          }
          continue;
        }

        if (range.action === "moveRange") {
          const destinationSheetName =
            range.moveRange?.destinationSheetName ?? enclosingSheetName;
          const destination = resolveWorksheetAddress(
            destinationSheetName,
            range.moveRange?.destination ?? "",
          );
          if (
            sameSourceSheet ||
            destination.sheetName.trim().toLowerCase() ===
              parsed.sheetName.toLowerCase()
          ) {
            return {
              error:
                `Refusing moveRange involving cited worksheet "${parsed.sheetName}". ` +
                "Remove its source Notes before moving cells so provenance cannot detach.",
              allowBestEffortValueWrite: false,
            };
          }
          continue;
        }

        if (range.action === "sort" && containsCell) {
          return {
            error:
              `Refusing to sort over cited cell ${cell}. ` +
              "Remove its source Note first so provenance cannot move to another value.",
            allowBestEffortValueWrite: false,
          };
        }

        if (range.action === "fill" && containsCell) {
          changesContent = true;
          if (!explicitlyRetiredNotes.has(normalizedCell)) {
            deferredContentWriteIssue ??= {
              error:
                `Refusing an unsourced range fill over cited cell ${cell}. ` +
                "Carry aligned citation IDs into the replacement fill, or explicitly remove/replace its source Note.",
              allowBestEffortValueWrite: true,
            };
          }
          continue;
        }

        if (range.action === "clear" && containsCell) {
          const clearType = range.clear?.clearType ?? "All";
          const rangeChangesContent =
            clearType === "All" || clearType === "Contents";
          if (rangeChangesContent) {
            changesContent = true;
          }
          if (
            rangeChangesContent &&
            !explicitlyRetiredNotes.has(normalizedCell)
          ) {
            deferredContentWriteIssue ??= {
              error:
                `Refusing a content clear over cited cell ${cell} without also removing its source Note.`,
              allowBestEffortValueWrite: true,
            };
          }
        }
      }
    }
    if (
      explicitlyRetiredNotes.has(normalizedCell) &&
      !changesContent
    ) {
      return {
        error:
          `Refusing to remove or replace the source Note on cited cell ${cell} ` +
          "while its cited content remains. Replace or clear the cell content in the same apply.",
        allowBestEffortValueWrite: false,
      };
    }
  }
  return deferredContentWriteIssue;
}

function retireExplicitlyChangedCitationNotes(
  ctx: ExecutorToolRuntimeContext,
  modifications: WorkbookModification,
  citedTargets: readonly CitationNoteTarget[],
): void {
  const provenance = ctx.accumulators.citationCellProvenance;
  if (!provenance) return;
  explicitCitationRetirementCells(
    modifications,
    citedTargets,
    provenance,
  ).forEach((cell) => {
    deleteCitationProvenance(provenance, cell);
  });
}

// ---------------------------------------------------------------------------
// Normalization and validation
// ---------------------------------------------------------------------------

/**
 * Normalizes raw model input. For resilience during early prompt iteration,
 * this also accepts a direct `WorkbookModification` payload and wraps it as
 * `{ modifications }` if the model omits the outer tool envelope.
 *
 * @param raw - Parsed JSON from the model.
 * @returns Normalized `modify_workbook` input.
 */
function normaliseModifyWorkbookInput(raw: unknown): ModifyWorkbookInput {
  if (raw === null || typeof raw !== "object") {
    return { stepId: null, targetSheet: null, modifications: {} };
  }

  const record = raw as Record<string, unknown>;
  const rawModifications =
    record.modifications !== null &&
    typeof record.modifications === "object"
      ? record.modifications
      : record;

  return {
    stepId: normaliseOptionalString(record.stepId),
    targetSheet: normaliseOptionalString(record.targetSheet),
    modifications: rawModifications as WorkbookModification,
  };
}

/**
 * Normalizes an optional string field, trimming whitespace and converting blank
 * values to null.
 *
 * @param value - Raw model field value.
 * @returns Trimmed string or null.
 */
function normaliseOptionalString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normaliseRecord(raw: unknown): Record<string, unknown> {
  return raw !== null && typeof raw === "object" ? raw as Record<string, unknown> : {};
}

function normaliseStepId(record: Record<string, unknown>): string | null {
  return normaliseOptionalString(record.stepId);
}

function normaliseStringField(record: Record<string, unknown>, field: string): string {
  return normaliseOptionalString(record[field]) ?? "";
}

function normaliseSetRangeValuesInput(raw: unknown): SetRangeValuesInput {
  const r = normaliseRecord(raw);
  return {
    stepId: normaliseStepId(r),
    sheetName: normaliseStringField(r, "sheetName"),
    address: normaliseStringField(r, "address"),
    values: Array.isArray(r.values) ? r.values as PrimitiveCellValue[][] : [],
    citationIdsMatrix: Array.isArray(r.citationIdsMatrix)
      ? r.citationIdsMatrix as CitationIdsMatrix
      : undefined,
  };
}

function normaliseSetRangeFormulasInput(raw: unknown): SetRangeFormulasInput {
  const r = normaliseRecord(raw);
  return {
    stepId: normaliseStepId(r),
    sheetName: normaliseStringField(r, "sheetName"),
    address: normaliseStringField(r, "address"),
    formulas: Array.isArray(r.formulas) ? r.formulas as (string | null)[][] : [],
  };
}

function normaliseFormatRangeInput(raw: unknown): FormatRangeInput {
  const r = normaliseRecord(raw);
  return {
    stepId: normaliseStepId(r),
    sheetName: normaliseStringField(r, "sheetName"),
    address: normaliseStringField(r, "address"),
    format: r.format !== null && typeof r.format === "object"
      ? compactNullableRecord(r.format) as CellFormat
      : {} as CellFormat,
  };
}

/** Remove strict-schema null placeholders before dispatching to host adapters. */
function compactNullableRecord(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(compactNullableRecord);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, nested]) => nested !== null)
      .map(([key, nested]) => [key, compactNullableRecord(nested)]),
  );
}

function normaliseCreateSheetInput(raw: unknown): CreateSheetInput {
  const r = normaliseRecord(raw);
  return {
    stepId: normaliseStepId(r),
    name: normaliseStringField(r, "name"),
    activate: r.activate === true,
  };
}

function normaliseRenameSheetInput(raw: unknown): RenameSheetInput {
  const r = normaliseRecord(raw);
  return {
    stepId: normaliseStepId(r),
    name: normaliseStringField(r, "name"),
    newName: normaliseStringField(r, "newName"),
  };
}

function normaliseActivateSheetInput(raw: unknown): ActivateSheetInput {
  const r = normaliseRecord(raw);
  return {
    stepId: normaliseStepId(r),
    name: normaliseStringField(r, "name"),
  };
}

function validateRangeBase(input: GranularRangeBaseInput): string | null {
  if (!input.sheetName) return "Missing required parameter: sheetName";
  if (!input.address) return "Missing required parameter: address";
  if (input.address.includes("!")) {
    return "address must be an unqualified local A1 range; use sheetName for worksheet routing";
  }
  if (!parseCellRangeAddress(input.address)) {
    return `Invalid address: "${input.address}". Expected a non-reversed A1 cell or rectangular range such as A1 or A1:B10`;
  }
  return null;
}

function unexpectedKey(
  value: unknown,
  allowed: readonly string[],
  path: string,
): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "object" || Array.isArray(value)) {
    return `${path} must be an object or null`;
  }
  const key = Object.keys(value as Record<string, unknown>).find(
    (candidate) => !allowed.includes(candidate),
  );
  return key ? `Unsupported ${path} field: ${key}` : null;
}

/**
 * Runtime companion to OpenAI strict decoding. Anthropic treats tool schemas
 * as advisory, so reject open or malformed formatting objects before dispatch.
 */
function validateFocusedFormat(value: unknown): string | null {
  const rootError = unexpectedKey(
    value,
    ["font", "fill", "borders", "alignment", "numberFormat"],
    "format",
  );
  if (rootError) return rootError;
  const format = normaliseRecord(value);
  const checks: Array<[unknown, readonly string[], string]> = [
    [
      format.font,
      ["name", "size", "color", "bold", "italic", "underline", "strikethrough"],
      "format.font",
    ],
    [format.fill, ["color", "pattern"], "format.fill"],
    [
      format.borders,
      ["top", "bottom", "left", "right"],
      "format.borders",
    ],
    [
      format.alignment,
      ["horizontal", "vertical", "wrapText", "indent", "textRotation"],
      "format.alignment",
    ],
    [format.numberFormat, ["format"], "format.numberFormat"],
  ];
  for (const [nested, allowed, path] of checks) {
    const error = unexpectedKey(nested, allowed, path);
    if (error) return error;
  }
  const borders = normaliseRecord(format.borders);
  for (const side of ["top", "bottom", "left", "right"]) {
    const error = unexpectedKey(borders[side], ["style", "color"], `format.borders.${side}`);
    if (error) return error;
  }
  return null;
}

function isValidCellValue(v: unknown): boolean {
  return v === null || typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

function validateValueMatrix(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) {
    return "Missing required parameter: values";
  }
  const first = value[0];
  if (!Array.isArray(first) || first.length === 0) {
    return "Invalid values: each row must be a non-empty array";
  }
  const width = first.length;
  for (let r = 0; r < value.length; r++) {
    const row = value[r];
    if (!Array.isArray(row)) {
      return `Invalid values: row ${r} is not an array`;
    }
    if (row.length !== width) {
      return `Invalid values: row ${r} has ${row.length} columns, expected ${width} (rectangular matrix required)`;
    }
    for (let c = 0; c < row.length; c++) {
      if (!isValidCellValue(row[c])) {
        return `Invalid values[${r}][${c}]: expected string, number, boolean, or null`;
      }
    }
  }
  return null;
}

function validateFormulaMatrix(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) {
    return "Missing required parameter: formulas";
  }
  const first = value[0];
  if (!Array.isArray(first) || first.length === 0) {
    return "Invalid formulas: each row must be a non-empty array";
  }
  const width = first.length;
  for (let r = 0; r < value.length; r++) {
    const row = value[r];
    if (!Array.isArray(row)) {
      return `Invalid formulas: row ${r} is not an array`;
    }
    if (row.length !== width) {
      return `Invalid formulas: row ${r} has ${row.length} columns, expected ${width} (rectangular matrix required)`;
    }
    for (let c = 0; c < row.length; c++) {
      if (row[c] !== null && typeof row[c] !== "string") {
        return `Invalid formulas[${r}][${c}]: expected string or null`;
      }
    }
  }
  return null;
}

function findRangeMatrixDimensionError(
  modifications: WorkbookModification,
): string | null {
  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = worksheet.sheetName?.trim() || "(unknown sheet)";
    for (const range of worksheet.ranges ?? []) {
      if (range.action !== "fill") continue;
      const hasValues = range.values !== undefined;
      const hasFormulas = range.formulas !== undefined;
      if (!hasValues && !hasFormulas) continue;
      if (hasValues && hasFormulas) {
        return (
          `Invalid range fill at ${sheetName}!${range.address}: provide either ` +
          "values or formulas, not both."
        );
      }

      const parsedRange = parseCellRange(range.address);
      if (!parsedRange) {
        return `Invalid range address for fill: ${sheetName}!${range.address}`;
      }
      const expectedRows = parsedRange.endRow - parsedRange.startRow + 1;
      const expectedColumns =
        parsedRange.endColumn - parsedRange.startColumn + 1;
      const matrix = hasFormulas ? range.formulas : range.values;
      const matrixError = hasFormulas
        ? validateFormulaMatrix(matrix)
        : validateValueMatrix(matrix);
      if (matrixError) {
        return `Invalid range fill at ${sheetName}!${range.address}: ${matrixError}`;
      }
      const actualRows = matrix!.length;
      const actualColumns = matrix![0]?.length ?? 0;
      if (
        actualRows !== expectedRows ||
        actualColumns !== expectedColumns
      ) {
        return (
          `Range/matrix dimension mismatch at ${sheetName}!${range.address}: ` +
          `target is ${expectedRows}x${expectedColumns}, but ` +
          `${hasFormulas ? "formulas" : "values"} are ` +
          `${actualRows}x${actualColumns}. Exact dimensions are required; ` +
          "broadcasting, padding, and truncation are not supported."
        );
      }
    }
  }
  return null;
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function validateNonEmptyObject(value: unknown, field: string): string | null {
  return value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0
    ? null
    : `Missing required parameter: ${field}`;
}

function granularValidationError(stepId: string | null, error: string): string {
  return JSON.stringify({
    ok: false,
    applied: false,
    stepId,
    error,
  } satisfies ModifyWorkbookResult);
}

function rectangularMatrixShape(value: unknown): MatrixShape | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const first = value[0];
  if (!Array.isArray(first) || first.length === 0) return undefined;
  const columns = first.length;
  if (value.some((row) => !Array.isArray(row) || row.length !== columns)) {
    return undefined;
  }
  return { rows: value.length, columns };
}

function compareRangeAndMatrixShape(
  address: unknown,
  matrix: unknown,
  field: "values" | "formulas",
  location: string,
): RangeMatrixShapeIssue | undefined {
  if (typeof address !== "string" || address.includes("!")) return undefined;
  const target = parseCellRangeAddress(address);
  const actualShape = rectangularMatrixShape(matrix);
  if (!target || !actualShape) return undefined;
  const expectedShape = {
    rows: target.rowCount,
    columns: target.columnCount,
  };
  return expectedShape.rows === actualShape.rows &&
    expectedShape.columns === actualShape.columns
    ? undefined
    : {
        location,
        address: target.normalizedAddress,
        field,
        expectedShape,
        actualShape,
      };
}

function findRangeMatrixShapeIssue(
  modifications: WorkbookModification,
): RangeMatrixShapeIssue | undefined {
  for (let worksheetIndex = 0;
    worksheetIndex < (modifications.worksheets?.length ?? 0);
    worksheetIndex += 1) {
    const worksheet = modifications.worksheets![worksheetIndex];
    for (let rangeIndex = 0;
      rangeIndex < (worksheet.ranges?.length ?? 0);
      rangeIndex += 1) {
      const range = worksheet.ranges![rangeIndex];
      if (range.action !== "fill") continue;
      const location =
        `modifications.worksheets[${worksheetIndex}].ranges[${rangeIndex}]`;
      if (range.values !== undefined) {
        const issue = compareRangeAndMatrixShape(
          range.address,
          range.values,
          "values",
          location,
        );
        if (issue) return issue;
      }
      if (range.formulas !== undefined) {
        const issue = compareRangeAndMatrixShape(
          range.address,
          range.formulas,
          "formulas",
          location,
        );
        if (issue) return issue;
      }
    }
  }
  return undefined;
}

function rangeMatrixShapeError(
  stepId: string | null,
  issue: RangeMatrixShapeIssue,
  toolName: "modify_workbook" | "set_range_values" | "set_range_formulas",
): string {
  const error =
    `${issue.location}.${issue.field}: target is ` +
    `${issue.expectedShape.rows}x${issue.expectedShape.columns}, but ` +
    `${issue.field} are ${issue.actualShape.rows}x${issue.actualShape.columns} ` +
    `at ${issue.address}. Supply exactly one matrix entry per ` +
    "target cell or narrow the address to the supplied matrix.";
  return JSON.stringify({
    ok: false,
    applied: false,
    stepId,
    code: RANGE_MATRIX_SHAPE_MISMATCH_CODE,
    error,
    retryable: true,
    retryWith: "same_tool",
    matrixField: issue.field,
    targetAddress: issue.address,
    expectedShape: issue.expectedShape,
    actualShape: issue.actualShape,
    instruction:
      `Correct the address or ${issue.field} dimensions, then call ` +
      `${toolName} exactly once with the same intended workbook content. ` +
      "Do not replace formulas with static values.",
  } satisfies ModifyWorkbookResult);
}

function validateRangeFillMatrices(
  modifications: WorkbookModification,
): string | null {
  for (let worksheetIndex = 0;
    worksheetIndex < (modifications.worksheets?.length ?? 0);
    worksheetIndex += 1) {
    const worksheet = modifications.worksheets![worksheetIndex];
    for (let rangeIndex = 0;
      rangeIndex < (worksheet.ranges?.length ?? 0);
      rangeIndex += 1) {
      const range = worksheet.ranges![rangeIndex];
      if (range.action !== "fill") continue;
      const location =
        `modifications.worksheets[${worksheetIndex}].ranges[${rangeIndex}]`;
      if (typeof range.address !== "string") {
        return `${location}.address must be a non-reversed A1 cell or rectangular range`;
      }
      if (range.address.includes("!")) {
        return `${location}.address must be an unqualified local A1 range; use the enclosing sheetName for worksheet routing`;
      }
      if (!parseCellRangeAddress(range.address)) {
        return `${location}.address must be a non-reversed A1 cell or rectangular range`;
      }
      if (range.values === undefined && range.formulas === undefined) {
        return `${location} action "fill" requires a non-empty values or formulas matrix`;
      }
      if (range.values !== undefined && range.formulas !== undefined) {
        return `${location} action "fill" accepts exactly one of values or formulas; supplying both is ambiguous`;
      }
      if (range.values !== undefined) {
        const error = validateValueMatrix(range.values);
        if (error) return `${location}.${error}`;
      }
      if (range.formulas !== undefined) {
        const error = validateFormulaMatrix(range.formulas);
        if (error) return `${location}.${error}`;
      }
      const issue =
        compareRangeAndMatrixShape(
          range.address,
          range.values,
          "values",
          location,
        ) ??
        compareRangeAndMatrixShape(
          range.address,
          range.formulas,
          "formulas",
          location,
        );
      if (issue) {
        return (
          `${location}.${issue.field} dimensions ` +
          `${issue.actualShape.rows}×${issue.actualShape.columns} must match ` +
          `target ${issue.address} dimensions ` +
          `${issue.expectedShape.rows}×${issue.expectedShape.columns}`
        );
      }
    }
  }
  return null;
}

/**
 * Validates the collection structure needed for safe normalization and
 * downstream traversal. This preflight deliberately excludes action semantics:
 * missing cell actions must first be repaired before the full safety validation
 * can evaluate them.
 *
 * @param input - Parsed `modify_workbook` input whose runtime collection shape
 * must be checked before missing cell actions are normalized.
 * @returns A user-facing validation error for the first malformed collection
 * or entry; otherwise `null` when normalization can safely traverse the input.
 */
function validateModifyWorkbookStructure(
  input: ModifyWorkbookInput,
): string | null {
  const modifications = input.modifications;
  if (!isObjectRecord(modifications)) {
    return "Missing required parameter: modifications";
  }

  for (const field of [
    "sheets",
    "worksheets",
    "annotations",
    "workbookOperations",
  ] as const) {
    const error = validateOptionalArray(modifications, field);
    if (error) return error;
  }

  if (!Array.isArray(modifications.worksheets)) return null;
  for (
    let worksheetIndex = 0;
    worksheetIndex < modifications.worksheets.length;
    worksheetIndex += 1
  ) {
    const worksheet = modifications.worksheets[worksheetIndex];
    const worksheetPath = `modifications.worksheets[${worksheetIndex}]`;
    if (!isObjectRecord(worksheet)) {
      return `Invalid ${worksheetPath}: expected an object`;
    }

    for (const field of [
      "cells",
      "ranges",
      "tables",
      "charts",
      "logos",
      "activeGraphs",
      "shapes",
    ] as const) {
      const error = validateOptionalArray(worksheet, field, worksheetPath);
      if (error) return error;
    }

    if (!Array.isArray(worksheet.cells)) continue;
    for (let cellIndex = 0; cellIndex < worksheet.cells.length; cellIndex += 1) {
      const cell = worksheet.cells[cellIndex];
      if (!isObjectRecord(cell)) {
        return `Invalid ${worksheetPath}.cells[${cellIndex}]: expected an object`;
      }
    }
  }

  return null;
}

/**
 * Determines whether a runtime value is a non-null, non-array object record.
 *
 * @param value - Runtime value to inspect.
 * @returns `true` when `value` can be safely traversed as a string-keyed
 * object; otherwise `false`.
 */
function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Performs runtime validation for `modify_workbook` before any host apply is
 * attempted. This intentionally checks only structural invariants that would
 * otherwise cause confusing no-ops or expose internal-only surfaces.
 *
 * @param input - Normalized tool input.
 * @param context - Optional run-scoped safety data used to validate protected
 * targets, FactSet semantics, and prior workbook modifications.
 * @returns Error message when invalid; null when safe to apply.
 */
export function validateModifyWorkbookInput(
  input: ModifyWorkbookInput,
  context: ModifyWorkbookValidationContext = {},
): string | null {
  const structureError = validateModifyWorkbookStructure(input);
  if (structureError) return structureError;
  const mods = input.modifications as Record<string, unknown>;
  const rangeFillError = validateRangeFillMatrices(input.modifications);
  if (rangeFillError) return rangeFillError;

  if (containsLegacyAnnotationFields(mods)) {
    return "Unsupported legacy comment field: use the dedicated note/comment tools";
  }
  const annotationError = validateAnnotationOperations(mods);
  if (annotationError) return annotationError;
  if (containsUnsupportedSheetVisibility(mods)) {
    return "Unsupported sheet action: setVisibility is not exposed to modify_workbook yet";
  }
  if (containsUnsupportedShapes(mods)) {
    return "Unsupported worksheet field: shapes is not exposed to modify_workbook yet";
  }
  if (containsUnsupportedTables(mods)) {
    return "Unsupported worksheet field: tables is not exposed to modify_workbook because the active host bridge cannot apply it";
  }
  const unsupportedRangeAction = findUnsupportedRangeAction(mods);
  if (unsupportedRangeAction) {
    return `Unsupported range action: ${unsupportedRangeAction} is not exposed to modify_workbook yet`;
  }
  if (containsUnsupportedFreezePanes(mods)) {
    return "Unsupported worksheet field: freezePanes is not exposed to modify_workbook yet";
  }
  if (containsUnsupportedActiveGraphRefresh(mods)) {
    return "Unsupported activeGraphs field: refresh is not exposed to modify_workbook yet";
  }
  const unsupportedWorkbookOperation = findUnsupportedWorkbookOperation(mods);
  if (unsupportedWorkbookOperation) {
    return `Unsupported workbook operation: ${unsupportedWorkbookOperation} is not exposed to modify_workbook yet`;
  }
  const rangeMatrixError = findRangeMatrixDimensionError(input.modifications);
  if (rangeMatrixError) return rangeMatrixError;

  const fqlDateViolation = findFqlDatePolicyViolation(input.modifications);
  if (fqlDateViolation) {
    const target =
      fqlDateViolation.address !== undefined
        ? ` at ${fqlDateViolation.sheetName ? `${fqlDateViolation.sheetName}!` : ""}${fqlDateViolation.address}`
        : "";
    return `Invalid FQL date serialization${target}: ${fqlDateViolation.message}`;
  }

  const dateAxisIssue = findFactSetDateAxisIssue(input.modifications, context);
  if (dateAxisIssue) {
    const target = dateAxisIssue.sheetName || "the target worksheet";
    if (dateAxisIssue.reason === "unsupported") {
      return (
        `Invalid time-series Date axis on ${target}: use ` +
        "julian(<the exact matching value series>.dates). Do not use " +
        "JULIAN(P_DATE(...)) for the displayed Date column."
      );
    }
    if (dateAxisIssue.reason === "raw_key") {
      return (
        `Invalid default time-series Date axis on ${target}: plain .dates ` +
        "returns YYYYMMDD keys. Wrap the exact matching series as " +
        "julian(<series>.dates) so Excel receives true date serials and can " +
        "display them as dd-mmm-yyyy."
      );
    }
    if (dateAxisIssue.reason === "mismatch") {
      return (
        `Mismatched time-series Date axis on ${target}: the .dates formula must ` +
        "use the same FactSet function, identifier, ordered window, and frequency " +
        "as one of the adjacent value-series formulas."
      );
    }
    return (
      `Time-series table on ${target} has a Date column and FactSet value ` +
      "formulas but no matching julian(<series>.dates) formula. Derive it from " +
      "the exact returned value-series expression and write it into the Date column."
    );
  }

  const dateAxisFormatError = findFactSetDateAxisFormattingError(
    input.modifications,
    context,
  );
  if (dateAxisFormatError) return dateAxisFormatError;

  const pricePrecisionError = findPricePrecisionFormattingError(
    input.modifications,
    context,
  );
  if (pricePrecisionError) return pricePrecisionError;

  if (!hasAtLeastOneOperation(input.modifications)) {
    return "modify_workbook requires at least one sheet, worksheet, annotation, or workbook operation";
  }

  return null;
}

interface FactSetDateAxisIssue {
  sheetName: string;
  reason: "missing" | "unsupported" | "raw_key" | "mismatch";
}

function findFactSetDateAxisIssue(
  modifications: WorkbookModification,
  context: ModifyWorkbookValidationContext,
): FactSetDateAxisIssue | undefined {
  const dateHeaderPattern =
    /^(?:date|dates|date\s*\/\s*observation|observation date|trading dates?)$/i;
  const timeSeriesPattern =
    /\b(?:last|past|historical|time[- ]series|daily|weekly|monthly|quarterly|annual)\b|\b\d+\s+(?:trading\s+)?days?\b/i;
  const worksheetsByName = new Map<string, NonNullable<WorkbookModification["worksheets"]>>();
  for (const source of [context.priorModifications, modifications]) {
    for (const worksheet of source?.worksheets ?? []) {
      const sheetName = worksheet.sheetName || "";
      const worksheets = worksheetsByName.get(sheetName) ?? [];
      worksheets.push(worksheet);
      worksheetsByName.set(sheetName, worksheets);
    }
  }

  for (const [sheetName, worksheets] of Array.from(worksheetsByName.entries())) {
    const headerValues: unknown[] = [];
    const intentValues: unknown[] = [];
    const formulaValues: unknown[] = [];

    for (const worksheet of worksheets) {
      for (const cell of worksheet.cells ?? []) {
        headerValues.push(cell.value);
        intentValues.push(cell.value, cell.ttfQuery);
        formulaValues.push(cell.formula, cell.value);
      }
      for (const range of worksheet.ranges ?? []) {
        for (const row of range.values ?? []) {
          headerValues.push(...row);
          intentValues.push(...row);
          formulaValues.push(...row);
        }
        for (const row of range.formulas ?? []) {
          formulaValues.push(...row);
        }
      }
      for (const table of worksheet.tables ?? []) {
        headerValues.push(...(table.headers ?? []));
        intentValues.push(...(table.headers ?? []));
        for (const row of table.rows ?? []) {
          intentValues.push(...row);
          formulaValues.push(...row);
        }
      }
    }

    const hasDateHeader = headerValues.some(
      (value) =>
        typeof value === "string" && dateHeaderPattern.test(value.trim()),
    );
    const hasTimeSeriesIntent = intentValues.some(
      (value) =>
        typeof value === "string" && timeSeriesPattern.test(value),
    ) || timeSeriesPattern.test(
      `${context.userRequest ?? ""} ${context.intentText ?? ""}`,
    );
    const fdsFormulas = formulaValues.filter(isFdsBoundFormula) as string[];
    const serialDateAxes = fdsFormulas.filter(
      (formula) =>
        /\.DATES\b/i.test(formula) &&
        /\bJULIAN\s*\(/i.test(formula) &&
        !/\bP_DATE\s*\(/i.test(formula),
    );
    const rawDateAxes = fdsFormulas.filter(
      (formula) =>
        /\.DATES\b/i.test(formula) &&
        !/\bJULIAN\s*\(|\bP_DATE\s*\(/i.test(formula),
    );
    const unsupportedDateAxes = fdsFormulas.filter(
      (formula) =>
        /\bP_DATE\s*\(/i.test(formula) ||
        (/\bJULIAN\s*\(/i.test(formula) && !/\.DATES\b/i.test(formula)),
    );
    const valueFormulas = fdsFormulas.filter(
      (formula) =>
        !/\.DATES\b/i.test(formula) &&
        !/\bJULIAN\s*\(|\bP_DATE\s*\(/i.test(formula),
    );

    if (!hasDateHeader || !hasTimeSeriesIntent || valueFormulas.length === 0) continue;
    if (unsupportedDateAxes.length > 0) {
      return { sheetName, reason: "unsupported" };
    }
    const rawDateKeysRequested = /\b(?:raw\s+)?YYYYMMDD\b/i.test(
      context.userRequest ?? "",
    );
    const acceptedDateAxes = rawDateKeysRequested
      ? [...serialDateAxes, ...rawDateAxes]
      : serialDateAxes;
    if (acceptedDateAxes.length === 0 && rawDateAxes.length > 0) {
      return { sheetName, reason: "raw_key" };
    }
    if (acceptedDateAxes.length === 0) {
      return { sheetName, reason: "missing" };
    }
    const hasMatchingDateAxis = acceptedDateAxes.some((axisFormula) => {
      const matchingValueFormula = normalizeFactSetSeriesFormula(axisFormula);
      return valueFormulas.some(
        (valueFormula) =>
          normalizeFactSetSeriesFormula(valueFormula) === matchingValueFormula,
      );
    });
    if (!hasMatchingDateAxis) {
      return { sheetName, reason: "mismatch" };
    }
  }
  return undefined;
}

function normalizeFactSetSeriesFormula(formula: string): string {
  const args = extractFdsFormulaArguments(formula);
  const identifier = args?.[args.length - 2] ?? "";
  const fqlArgument = extractFqlArgumentExpression(formula) ?? formula;
  const normalizedFql = fqlArgument
    .replace(/\bJULIAN\s*\(/i, "")
    .replace(/\.DATES\s*\)/i, "")
    .replace(/\.DATES\b/i, "")
    .replace(/\s+/g, "")
    .toUpperCase();
  return `${identifier.replace(/\s+/g, "").toUpperCase()}|${normalizedFql}`;
}

function findFactSetDateAxisFormattingError(
  modifications: WorkbookModification,
  context: ModifyWorkbookValidationContext,
): string | null {
  const targets = [...(context.fdsDateAxisWriteTargets ?? [])];
  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = worksheet.sheetName ?? "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      const formula = isFdsDateAxisFormula(cell.formula)
        ? cell.formula
        : cell.value;
      const kind = fdsDateAxisKind(formula);
      if (typeof cell.address === "string" && kind) {
        targets.push({ sheetName, address: cell.address, kind });
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (range.action !== "fill") continue;
      const writeCells = enumerateRangeWriteCells(
        range.address,
        range.formulas,
        range.values,
      );
      for (const writeCell of writeCells ?? []) {
        if (writeCell.fdsDateAxisKind) {
          targets.push({
            sheetName,
            address: writeCell.address,
            kind: writeCell.fdsDateAxisKind,
          });
        }
      }
    }
  }
  if (targets.length === 0) return null;

  const invalidFormat = (
    sheetName: string,
    address: string,
    numberFormat: string | undefined,
  ): string | null => {
    if (!numberFormat) return null;
    const overlappingTargets = targets.filter(
      (target) =>
        target.sheetName.toLowerCase() === sheetName.toLowerCase() &&
        addressesOverlap(target.address, address),
    );
    if (overlappingTargets.length === 0) return null;

    const dateFormatPreference = findRequestedDateAxisFormat(
      context.userRequest,
    );
    for (const target of overlappingTargets) {
      if (target.kind === "key") {
        if (isFactSetDateKeyNumberFormat(numberFormat)) continue;
        return (
          `Raw FactSet .dates output at ${sheetName}!${address} returns ` +
          `YYYYMMDD keys. Format that spill as 0000-00-00, or use the default ` +
          `julian(<series>.dates) axis for a true dd-mmm-yyyy Excel date.`
        );
      }
      if (
        dateFormatPreference.kind === "custom"
          ? isDateNumberFormat(numberFormat)
          : normalizeDateNumberFormat(numberFormat) ===
            normalizeDateNumberFormat(dateFormatPreference.format)
      ) {
        continue;
      }
      const expected =
        dateFormatPreference.kind === "custom"
          ? "the date format explicitly requested by the user"
          : dateFormatPreference.format;
      return (
        `FactSet date-axis format at ${sheetName}!${address} must use ${expected}. ` +
        `julian(<series>.dates) returns true Excel serial dates; apply the ` +
        `format to the entire spill range.`
      );
    }
    return null;
  };

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = worksheet.sheetName ?? "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      const error = invalidFormat(
        sheetName,
        cell.address,
        cell.format?.numberFormat?.format,
      );
      if (error) return error;
    }
    for (const range of worksheet.ranges ?? []) {
      const error = invalidFormat(
        sheetName,
        range.address,
        range.format?.numberFormat?.format,
      );
      if (error) return error;
    }
  }
  return null;
}

function isFactSetDateKeyNumberFormat(format: string): boolean {
  const primarySection = format
    .split(";")[0]
    .replace(/"([-\/])"/g, "$1")
    .replace(/\\([-\/])/g, "$1")
    .replace(/\s+/g, "");
  return /^0000-00-00$/.test(primarySection);
}

type DateAxisFormatPreference =
  | { kind: "exact"; format: string }
  | { kind: "custom" };

function findRequestedDateAxisFormat(
  request: string | undefined,
): DateAxisFormatPreference {
  if (!request) return { kind: "exact", format: "dd-mmm-yyyy" };
  const symbolic = request.match(
    /\b(dd-mmm-yyyy|d-mmm-yyyy|mm\/dd\/yyyy|m\/d\/yyyy|dd\/mm\/yyyy|d\/m\/yyyy|yyyy-mm-dd)\b/i,
  );
  if (symbolic) {
    return { kind: "exact", format: symbolic[1].toLowerCase() };
  }
  if (
    /\b(?:display|format|show)\b[\s\S]{0,40}\bdates?\b[\s\S]{0,20}\b(?:as|in)\b/i.test(
      request,
    ) &&
    /\b\d{1,2}-[A-Za-z]{3}-\d{4}\b/.test(request)
  ) {
    return { kind: "exact", format: "dd-mmm-yyyy" };
  }
  if (
    /\b(?:display|format|show)\b[\s\S]{0,40}\bdates?\b[\s\S]{0,20}\b(?:as|in)\b/i.test(
      request,
    )
  ) {
    return { kind: "custom" };
  }
  return { kind: "exact", format: "dd-mmm-yyyy" };
}

function normalizeDateNumberFormat(format: string): string {
  return format
    .split(";")[0]
    .replace(/"([^"]*)"/g, "$1")
    .replace(/\\(.)/g, "$1")
    .replace(/\s+/g, "")
    .toLowerCase();
}

function findPricePrecisionFormattingError(
  modifications: WorkbookModification,
  context: ModifyWorkbookValidationContext,
): string | null {
  if (
    !/\b(?:price|quote|closing|opening|intraday|bid|ask|vwap)\b/i.test(
      `${context.userRequest ?? ""} ${context.intentText ?? ""}`,
    )
  ) {
    return null;
  }

  const excludedTargets = [
    ...(context.fdsDateAxisWriteTargets ?? []),
    ...(context.factSetPercentScaleTargets ?? []),
  ];
  const targets = (context.fdsFormulaWriteTargets ?? []).filter(
    (target) =>
      !excludedTargets.some(
        (excluded) =>
          excluded.sheetName.toLowerCase() === target.sheetName.toLowerCase() &&
          addressesOverlap(excluded.address, target.address),
      ),
  );
  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = worksheet.sheetName ?? "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      if (
        typeof cell.address === "string" &&
        (isFdsBoundFormula(cell.formula) || isFdsBoundFormula(cell.value)) &&
        !isFdsDateAxisFormula(cell.formula) &&
        !isFdsDateAxisFormula(cell.value)
      ) {
        targets.push({ sheetName, address: cell.address });
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (range.action !== "fill") continue;
      const writeCells = enumerateRangeWriteCells(
        range.address,
        range.formulas,
        range.values,
      );
      for (const writeCell of writeCells ?? []) {
        if (writeCell.hasFdsFormula && !writeCell.fdsDateAxisKind) {
          targets.push({ sheetName, address: writeCell.address });
        }
      }
    }
  }
  const requestedDecimalPlaces = findRequestedDecimalPlaces(
    context.userRequest,
  );
  const invalidFormat = (
    sheetName: string,
    address: string,
    numberFormat: string | undefined,
  ): string | null => {
    if (!numberFormat || isDateNumberFormat(numberFormat)) return null;
    // A percent display is never a raw market-price quote, so the two-decimal
    // default does not apply even without registry provenance for the cell.
    if (
      hasMultiplyingPercentToken(numberFormat) ||
      hasLiteralPercentToken(numberFormat)
    ) {
      return null;
    }
    const overlapsFactSetOutput = targets.some(
      (target) =>
        target.sheetName.toLowerCase() === sheetName.toLowerCase() &&
        addressesOverlap(target.address, address),
    );
    if (!overlapsFactSetOutput) return null;

    const actualDecimalPlaces = decimalPlacesInNumberFormat(numberFormat);
    if (requestedDecimalPlaces !== undefined) {
      return actualDecimalPlaces === requestedDecimalPlaces
        ? null
        : (
            `Market-price format at ${sheetName}!${address} does not match the ` +
            `user's requested ${requestedDecimalPlaces} decimal place(s). Apply ` +
            `${numberFormatForDecimalPlaces(requestedDecimalPlaces)} to the entire ` +
            "price/quote output spill range without rounding the formula."
          );
    }
    return actualDecimalPlaces === 2
      ? null
      : (
          `Market-price format at ${sheetName}!${address} uses ` +
          `${actualDecimalPlaces ?? "an unspecified number of"} decimal places. ` +
          "Use the default 0.00 format on the entire price/quote output spill " +
          "range unless the user explicitly requests another precision."
        );
  };

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = worksheet.sheetName ?? "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      const error = invalidFormat(
        sheetName,
        cell.address,
        cell.format?.numberFormat?.format,
      );
      if (error) return error;
    }
    for (const range of worksheet.ranges ?? []) {
      const error = invalidFormat(
        sheetName,
        range.address,
        range.format?.numberFormat?.format,
      );
      if (error) return error;
    }
  }
  return null;
}

function findRequestedDecimalPlaces(request: string | undefined): number | undefined {
  if (!request) return undefined;
  const numericMatch = request.match(
    /\b(\d{1,2})\s*(?:decimal(?:s|\s+places?)?|d\.?\s*p\.?)\b/i,
  );
  if (numericMatch) {
    const value = Number(numericMatch[1]);
    return value <= 15 ? value : undefined;
  }

  const words: Record<string, number> = {
    no: 0,
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
  };
  const wordMatch = request.match(
    /\b(no|zero|one|two|three|four|five|six|seven|eight|nine|ten)\s+decimal(?:s|\s+places?)?\b/i,
  );
  return wordMatch ? words[wordMatch[1].toLowerCase()] : undefined;
}

function stripNumberFormatLiterals(format: string): string {
  return format
    .split(";")[0]
    .replace(/"[^"]*"/g, "")
    .replace(/\\./g, "")
    .replace(/\[[^\]]*\]/g, "")
    .replace(/_.?/g, "")
    .replace(/\*./g, "");
}

function isDateNumberFormat(format: string): boolean {
  return /[dy]/i.test(stripNumberFormatLiterals(format));
}

function decimalPlacesInNumberFormat(format: string): number | undefined {
  const normalized = stripNumberFormatLiterals(format);
  const decimalPoint = normalized.indexOf(".");
  if (decimalPoint < 0) {
    return /[0#?]/.test(normalized) ? 0 : undefined;
  }
  const fraction = normalized.slice(decimalPoint + 1).match(/^[0#?]+/);
  return fraction?.[0].length ?? 0;
}

function numberFormatForDecimalPlaces(decimalPlaces: number): string {
  return decimalPlaces === 0
    ? "0"
    : `0.${"0".repeat(decimalPlaces)}`;
}

/**
 * Validates that an optional object field is an array when present.
 *
 * @param record - Object containing the optional collection.
 * @param field - Field name to validate.
 * @param path - Human-readable path to `record`, used to identify malformed
 * nested collections in the returned error.
 * @returns A user-facing validation error when the field is present but is not
 * an array; otherwise `null`.
 */
function validateOptionalArray(
  record: Record<string, unknown>,
  field: string,
  path = "modifications",
): string | null {
  return record[field] !== undefined && !Array.isArray(record[field])
    ? `Invalid ${path}.${field}: expected an array`
    : null;
}

/**
 * Rejects the old ambiguous `cells[].comment` / `ranges[].comment` fields.
 * Threaded comments and notes now have distinct dedicated tools and internal
 * annotation operations.
 */
function containsLegacyAnnotationFields(mods: Record<string, unknown>): boolean {
  const worksheets = mods.worksheets;
  if (!Array.isArray(worksheets)) return false;
  return worksheets.some((worksheet) => {
    if (worksheet === null || typeof worksheet !== "object") return false;
    const record = worksheet as Record<string, unknown>;
    return ["cells", "ranges"].some((field) => {
      const entries = record[field];
      return (
        Array.isArray(entries) &&
        entries.some(
          (entry) =>
            entry !== null &&
            typeof entry === "object" &&
            "comment" in (entry as Record<string, unknown>),
        )
      );
    });
  });
}

/** Validates the internal-only annotation operation union. */
function validateAnnotationOperations(mods: Record<string, unknown>): string | null {
  const annotations = mods.annotations;
  if (annotations === undefined) return null;
  if (!Array.isArray(annotations)) {
    return "Invalid modifications.annotations: expected an array";
  }
  for (let index = 0; index < annotations.length; index += 1) {
    const annotation = annotations[index];
    if (annotation === null || typeof annotation !== "object") {
      return `Invalid modifications.annotations[${index}]: expected an object`;
    }
    const operation = annotation as Record<string, unknown>;
    if (operation.kind !== "comment" && operation.kind !== "note") {
      return `Invalid modifications.annotations[${index}].kind: expected "comment" or "note"`;
    }
    if (operation.action !== "set" && operation.action !== "delete") {
      return `Invalid modifications.annotations[${index}].action: expected "set" or "delete"`;
    }
    if (
      typeof operation.sheetName !== "string" ||
      operation.sheetName.trim().length === 0
    ) {
      return `Invalid modifications.annotations[${index}].sheetName: expected a non-empty string`;
    }
    if (operation.action === "set") {
      if (
        typeof operation.address !== "string" ||
        operation.address.trim().length === 0
      ) {
        return `Invalid modifications.annotations[${index}].address: expected a non-empty string`;
      }
      if (
        typeof operation.text !== "string" ||
        operation.text.trim().length === 0
      ) {
        return `Invalid modifications.annotations[${index}].text: expected a non-empty string`;
      }
      if (
        operation.replaceExisting !== undefined &&
        typeof operation.replaceExisting !== "boolean"
      ) {
        return `Invalid modifications.annotations[${index}].replaceExisting: expected a boolean`;
      }
    } else if (
      !Array.isArray(operation.addresses) ||
      operation.addresses.length === 0 ||
      operation.addresses.some(
        (address) => typeof address !== "string" || address.trim().length === 0,
      )
    ) {
      return `Invalid modifications.annotations[${index}].addresses: expected at least one non-empty string`;
    }
  }
  return null;
}

/**
 * Checks whether the payload attempts to use the intentionally unexposed
 * `SheetModification.action = "setVisibility"` surface.
 *
 * @param mods - Raw modifications record.
 * @returns True when an unsupported visibility action is present.
 */
function containsUnsupportedSheetVisibility(mods: Record<string, unknown>): boolean {
  const sheets = mods.sheets;
  return Array.isArray(sheets) && sheets.some((s) => {
    return s !== null && typeof s === "object" && (s as Record<string, unknown>).action === "setVisibility";
  });
}

/**
 * Checks whether the payload attempts to use the intentionally unexposed
 * generic `WorksheetModification.shapes[]` surface.
 *
 * @param mods - Raw modifications record.
 * @returns True when `worksheets[].shapes` is present.
 */
function containsUnsupportedShapes(mods: Record<string, unknown>): boolean {
  const worksheets = mods.worksheets;
  return Array.isArray(worksheets) && worksheets.some((ws) => {
    return ws !== null && typeof ws === "object" && "shapes" in (ws as Record<string, unknown>);
  });
}

/**
 * The intent bridge currently reports table mutations as skipped. Keeping this
 * surface out of the executor avoids a model-visible "successful" write that
 * never reaches the workbook. Dedicated table tooling can restore it once the
 * bridge implements the same semantics as Office.js.
 */
function containsUnsupportedTables(mods: Record<string, unknown>): boolean {
  const worksheets = mods.worksheets;
  return Array.isArray(worksheets) && worksheets.some((ws) => {
    if (ws === null || typeof ws !== "object") return false;
    const tables = (ws as Record<string, unknown>).tables;
    return Array.isArray(tables) && tables.length > 0;
  });
}

/**
 * Finds range actions that exist in internal types but are not part of the
 * model-facing `modify_workbook` surface.
 *
 * @param mods - Raw modifications record.
 * @returns The first unsupported range action, or null when none is present.
 */
function findUnsupportedRangeAction(mods: Record<string, unknown>): string | null {
  const unsupported = new Set([
    "filter",
    "groupColumns",
    "groupRows",
    "ungroupColumns",
    "ungroupRows",
  ]);
  const worksheets = mods.worksheets;
  if (!Array.isArray(worksheets)) return null;
  for (const ws of worksheets) {
    if (ws === null || typeof ws !== "object") continue;
    const ranges = (ws as Record<string, unknown>).ranges;
    if (!Array.isArray(ranges)) continue;
    for (const range of ranges) {
      if (range === null || typeof range !== "object") continue;
      const action = (range as Record<string, unknown>).action;
      if (typeof action === "string" && unsupported.has(action)) {
        return action;
      }
    }
  }
  return null;
}

/**
 * Checks whether the payload attempts to use the intentionally unexposed
 * `WorksheetModification.freezePanes` surface.
 *
 * @param mods - Raw modifications record.
 * @returns True when `worksheets[].freezePanes` is present.
 */
function containsUnsupportedFreezePanes(mods: Record<string, unknown>): boolean {
  const worksheets = mods.worksheets;
  return Array.isArray(worksheets) && worksheets.some((ws) => {
    return ws !== null && typeof ws === "object" && "freezePanes" in (ws as Record<string, unknown>);
  });
}

/**
 * Checks whether a private ActiveGraph entry uses the internal `refresh` field.
 * ActiveGraphs are no longer exposed through `modify_workbook`, but the
 * validation path still protects non-agent/internal callers.
 *
 * @param mods - Raw modifications record.
 * @returns True when `worksheets[].activeGraphs[].refresh` is present.
 */
function containsUnsupportedActiveGraphRefresh(mods: Record<string, unknown>): boolean {
  const worksheets = mods.worksheets;
  if (!Array.isArray(worksheets)) return false;
  return worksheets.some((ws) => {
    if (ws === null || typeof ws !== "object") return false;
    const activeGraphs = (ws as Record<string, unknown>).activeGraphs;
    return Array.isArray(activeGraphs) && activeGraphs.some((graph) => {
      return graph !== null && typeof graph === "object" && "refresh" in (graph as Record<string, unknown>);
    });
  });
}

/**
 * Finds workbook operations that are wired internally but not exposed in the
 * model-facing `modify_workbook` surface.
 *
 * @param mods - Raw modifications record.
 * @returns The first unsupported workbook operation, or null when none is present.
 */
function findUnsupportedWorkbookOperation(mods: Record<string, unknown>): string | null {
  const unsupported = new Set(["insertTtfApiResponse"]);
  const workbookOperations = mods.workbookOperations;
  if (!Array.isArray(workbookOperations)) return null;
  for (const op of workbookOperations) {
    if (op === null || typeof op !== "object") continue;
    const action = (op as Record<string, unknown>).action;
    if (typeof action === "string" && unsupported.has(action)) {
      return action;
    }
  }
  return null;
}

/**
 * Determines whether a `WorkbookModification` has at least one actionable
 * top-level or worksheet-level operation.
 *
 * @param mods - Workbook modifications to inspect.
 * @returns True when the payload is not an empty/no-op message-only payload.
 */
function hasAtLeastOneOperation(mods: WorkbookModification): boolean {
  if ((mods.sheets?.length ?? 0) > 0) return true;
  if ((mods.annotations?.length ?? 0) > 0) return true;
  if ((mods.workbookOperations?.length ?? 0) > 0) return true;
  return (mods.worksheets ?? []).some((ws) => {
    return (
      (ws.cells?.length ?? 0) > 0 ||
      (ws.ranges?.length ?? 0) > 0 ||
      (ws.tables?.length ?? 0) > 0 ||
      (ws.charts?.length ?? 0) > 0 ||
      (ws.logos?.length ?? 0) > 0
    );
  });
}

// ---------------------------------------------------------------------------
// Accumulator helpers
// ---------------------------------------------------------------------------

/**
 * Resolves the step associated with a write.
 *
 * A `null` `stepId` is the documented signal for a run-level / recovery write
 * that is intentionally not tied to any plan step (see the tool schema). We
 * therefore must NOT fall back to `currentStepId` in that case: doing so would
 * silently bind the write to the active step and inherit its `targetSheet`,
 * landing run-level fixes on the wrong sheet. Only a non-null `stepId` resolves
 * to a step.
 *
 * @param ctx - Runtime context with current run steps.
 * @param stepId - Explicit step id from the tool call, or null for run-level.
 * @returns Matching step state, or undefined for run-level writes.
 */
export function resolveStep(
  ctx: ExecutorToolRuntimeContext,
  stepId: string | null,
): ExecutorStepState | undefined {
  return stepId ? ctx.planState.steps.find((s) => s.id === stepId) : undefined;
}

/**
 * Records a host apply result in the shared run accumulators.
 *
 * Writes no longer drive the step lifecycle: `the internal step lifecycle` is the single
 * source of truth for step status, result lines, and display text. This keeps
 * the "verify after write" discipline enforceable by the model and prevents a
 * single sub-write from prematurely terminalizing a multi-write step.
 *
 * Run-level writes (no associated step) still append a lightweight trace line,
 * since no `the internal step lifecycle` call will ever record them otherwise.
 *
 * @param ctx - Runtime context with mutable accumulators.
 * @param input - Normalized tool input.
 * @param step - Associated step, if any.
 * @param applyResult - Result returned by the host apply callback.
 */
function recordApplyResult(
  ctx: ExecutorToolRuntimeContext,
  input: ModifyWorkbookInput,
  step: ExecutorStepState | undefined,
  applyResult: {
    success: boolean;
    message: string;
    editedCells: string[];
    retryReason?: "stuck_edit" | "stuck_busy";
    skippedActions?: string[];
    failedActions?: string[];
    deferredRefreshAll?: boolean;
    deferredRecalcMode?: "recalculate" | "full" | "fullRebuild";
  },
): void {
  const confirmedModifications = applyResult.success
    ? input.modifications
    : confirmedWriteModifications(
        input.modifications,
        applyResult.editedCells ?? [],
      );
  const hasConfirmedEdits = (applyResult.editedCells?.length ?? 0) > 0;
  if (
    applyResult.success &&
    (applyResult.failedActions?.length ?? 0) === 0 &&
    (applyResult.skippedActions?.length ?? 0) === 0
  ) {
    retireClearedFdsFormulaWriteTargets(ctx.accumulators, input.modifications);
    ctx.factSetFormatRegistry?.commitSuccessfulModifications(
      input.modifications,
      ctx.session.pinnedWorkbookName,
    );
  }
  if (applyResult.success || hasConfirmedEdits) {
    ctx.accumulators.editedCells.push(...(applyResult.editedCells ?? []));
    ctx.accumulators.lastModifications = confirmedModifications;
    recordAutofitTargets(ctx.accumulators, confirmedModifications);
    recordColumnSizingIntent(ctx.accumulators, confirmedModifications);
    recordFormulaWriteTargets(ctx.accumulators, confirmedModifications);
    recordFdsFormulaWriteTargets(ctx.accumulators, confirmedModifications);
    recordFdsDateAxisWriteTargets(ctx.accumulators, confirmedModifications);
    for (const sheet of collectFdsBoundFormulaSheets(confirmedModifications)) {
      ctx.accumulators.fdsRefreshSheets.add(sheet);
    }
  }
  if (applyResult.deferredRefreshAll === true) {
    ctx.accumulators.deferredRefreshAllRequested = true;
  }
  if (applyResult.deferredRecalcMode) {
    ctx.accumulators.deferredRecalcMode = applyResult.deferredRecalcMode;
  }

  if (!step) {
    const message = applyResult.success
      ? applyResult.message || input.modifications.message || "Workbook modifications applied"
      : applyResult.retryReason
        ? applyResult.message || "Workbook modifications paused because Excel is not ready"
        : applyResult.message || input.modifications.message || "Workbook modifications failed";
    ctx.accumulators.stepResults.push(`modify_workbook. ${message}`);
  }
}

/**
 * Project a mixed/failed host result down to writes the host acknowledged.
 * `editedCells` is populated only after the corresponding host promise/sync
 * resolves, so this subset is safe to use for verification and refresh debt
 * without pretending failed siblings landed.
 */
function confirmedWriteModifications(
  modifications: WorkbookModification,
  editedCells: readonly string[],
): WorkbookModification {
  const candidateCounts = new Map<string, number>();
  const countCandidate = (address: unknown): void => {
    if (typeof address !== "string") return;
    const normalized = normalizeAddress(address);
    candidateCounts.set(
      normalized,
      (candidateCounts.get(normalized) ?? 0) + 1,
    );
  };
  for (const worksheet of modifications.worksheets ?? []) {
    for (const cell of worksheet.cells ?? []) countCandidate(cell.address);
    for (const range of worksheet.ranges ?? []) countCandidate(range.address);
    for (const table of worksheet.tables ?? []) countCandidate(table.address);
    for (const logo of worksheet.logos ?? []) countCandidate(logo.address);
  }
  const editedCounts = new Map<string, number>();
  for (const address of editedCells) {
    if (typeof address !== "string") continue;
    const normalized = normalizeAddress(address);
    editedCounts.set(normalized, (editedCounts.get(normalized) ?? 0) + 1);
  }
  const consumeEditedAddress = (address: unknown): boolean => {
    if (typeof address !== "string") return false;
    const normalized = normalizeAddress(address);
    if ((candidateCounts.get(normalized) ?? 0) !== 1) return false;
    const remaining = editedCounts.get(normalized) ?? 0;
    if (remaining <= 0) return false;
    if (remaining === 1) editedCounts.delete(normalized);
    else editedCounts.set(normalized, remaining - 1);
    return true;
  };
  const worksheets = (modifications.worksheets ?? [])
    .map((worksheet) => ({
      ...worksheet,
      cells: worksheet.cells?.filter((cell) =>
        consumeEditedAddress(cell.address),
      ),
      ranges: worksheet.ranges?.filter((range) =>
        consumeEditedAddress(range.address),
      ),
      tables: worksheet.tables?.filter((table) =>
        consumeEditedAddress(table.address),
      ),
      logos: worksheet.logos?.filter((logo) =>
        consumeEditedAddress(logo.address),
      ),
    }))
    .filter(
      (worksheet) =>
        (worksheet.cells?.length ?? 0) > 0 ||
        (worksheet.ranges?.length ?? 0) > 0 ||
        (worksheet.tables?.length ?? 0) > 0 ||
        (worksheet.logos?.length ?? 0) > 0,
    );
  return {
    ...modifications,
    sheets: undefined,
    annotations: undefined,
    workbookOperations: undefined,
    worksheets,
  };
}

function recordAutofitTargets(
  accumulators: ExecutorToolRuntimeContext["accumulators"],
  modifications: WorkbookModification,
): void {
  accumulators.autofitSheetNames ??= new Set<string>();
  accumulators.autofitTargets ??= [];
  const targetKeys = new Set(
    accumulators.autofitTargets.map(
      (target) => `${target.sheetName.toLowerCase()}!${target.address.toUpperCase()}`,
    ),
  );

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;
    const recordTarget = (addressValue: unknown) => {
      const address = typeof addressValue === "string" ? addressValue.trim() : "";
      if (!address) return;
      const key = `${sheetName.toLowerCase()}!${address.toUpperCase()}`;
      if (targetKeys.has(key)) return;
      targetKeys.add(key);
      accumulators.autofitTargets.push({ sheetName, address });
      accumulators.autofitSheetNames.add(sheetName);
    };

    for (const cell of worksheet.cells ?? []) {
      if (cell.action === "set") recordTarget(cell.address);
    }
    for (const range of worksheet.ranges ?? []) {
      if (range.action === "fill") recordTarget(range.address);
    }
  }
}

function recordFormulaWriteTargets(
  accumulators: ExecutorToolRuntimeContext["accumulators"],
  modifications: WorkbookModification,
): void {
  accumulators.formulaWriteTargets ??= [];
  const targetKeys = new Set(
    accumulators.formulaWriteTargets.map(
      (target) => `${target.sheetName.toLowerCase()}!${target.address.toUpperCase()}`,
    ),
  );

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      if (
        cell.action === "set" &&
        (isFormulaWrite(cell.formula) || isFormulaWrite(cell.value)) &&
        typeof cell.address === "string" &&
        cell.address.trim()
      ) {
        const address = cell.address.trim();
        const key = `${sheetName.toLowerCase()}!${address.toUpperCase()}`;
        if (!targetKeys.has(key)) {
          targetKeys.add(key);
          accumulators.formulaWriteTargets.push({ sheetName, address });
        }
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (
        range.action === "fill" &&
        (matrixHasFormulaWrite(range.formulas) || matrixHasFormulaWrite(range.values)) &&
        typeof range.address === "string" &&
        range.address.trim()
      ) {
        const address = range.address.trim();
        const key = `${sheetName.toLowerCase()}!${address.toUpperCase()}`;
        if (!targetKeys.has(key)) {
          targetKeys.add(key);
          accumulators.formulaWriteTargets.push({ sheetName, address });
        }
      }
    }
    for (const table of worksheet.tables ?? []) {
      if (
        table.action !== "create" ||
        !matrixHasFormulaWrite(table.rows) ||
        typeof table.address !== "string" ||
        !table.headers?.length
      ) {
        continue;
      }
      const tableAnchor = parseCellRange(table.address);
      if (!tableAnchor) continue;
      const address =
        `${excelColumnName(tableAnchor.startColumn)}${tableAnchor.startRow + 1}:` +
        `${excelColumnName(tableAnchor.startColumn + table.headers.length - 1)}` +
        `${tableAnchor.startRow + table.rows!.length + 1}`;
      const key = `${sheetName.toLowerCase()}!${address.toUpperCase()}`;
      if (!targetKeys.has(key)) {
        targetKeys.add(key);
        accumulators.formulaWriteTargets.push({ sheetName, address });
      }
    }
  }
}

/**
 * Percent-scale FactSet cells known to this run, from the format registry.
 *
 * `listSafetyCells` covers committed plus attempted writes, so a later
 * formatting-only call still resolves return cells written in an earlier step.
 */
function collectFactSetPercentScaleTargets(
  ctx: ExecutorToolRuntimeContext,
): { sheetName: string; address: string }[] {
  return (
    ctx.factSetFormatRegistry
      ?.listSafetyCells(ctx.session.pinnedWorkbookName)
      .filter(
        (cell) =>
          cell.hint.semantics === "percentage_points" ||
          cell.hint.semantics === "decimal_fraction",
      )
      .map((cell) => ({ sheetName: cell.sheetName, address: cell.address })) ??
    []
  );
}

function recordFdsFormulaWriteTargets(
  accumulators: ExecutorToolRuntimeContext["accumulators"],
  modifications: WorkbookModification,
): void {
  accumulators.fdsFormulaWriteTargets ??= [];
  accumulators.fdsFormulaWriteAttempts ??= {};
  const targetKeys = new Set(
    accumulators.fdsFormulaWriteTargets.map(
      (target) => `${target.sheetName.toLowerCase()}!${normalizeAddress(target.address)}`,
    ),
  );

  const record = (sheetName: string, address: string): void => {
    const key = `${sheetName.toLowerCase()}!${normalizeAddress(address)}`;
    accumulators.fdsFormulaWriteAttempts![key] =
      (accumulators.fdsFormulaWriteAttempts![key] ?? 0) + 1;
    if (targetKeys.has(key)) return;
    targetKeys.add(key);
    accumulators.fdsFormulaWriteTargets?.push({ sheetName, address });
  };

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      if (
        cell.action === "set" &&
        typeof cell.address === "string" &&
        cell.address.trim() &&
        (isFdsBoundFormula(cell.formula) || isFdsBoundFormula(cell.value))
      ) {
        record(sheetName, cell.address.trim());
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (
        range.action !== "fill" ||
        typeof range.address !== "string" ||
        !range.address.trim()
      ) {
        continue;
      }
      const writeCells = enumerateRangeWriteCells(
        range.address,
        range.formulas,
        range.values,
      );
      if (writeCells) {
        for (const writeCell of writeCells) {
          if (writeCell.hasFdsFormula) {
            record(sheetName, writeCell.address);
          }
        }
      } else if (
        matrixHasFdsFormula(range.formulas) ||
        matrixHasFdsFormula(range.values)
      ) {
        // Unknown/non-cell-shaped address: conservatively protect the range.
        record(sheetName, range.address.trim());
      }
    }
  }
}

/**
 * Retire run-scoped formula protection only after the host confirms a content
 * clear. The format registry already applies this transition, but the separate
 * overwrite-safety accumulator must track the same live workbook state.
 */
function retireClearedFdsFormulaWriteTargets(
  accumulators: ExecutorToolRuntimeContext["accumulators"],
  modifications: WorkbookModification,
): void {
  const clearedRanges: Array<{ sheetName: string; address: string }> = [];
  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName =
      typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      if (cell.action === "clear" && cell.address?.trim()) {
        clearedRanges.push({ sheetName, address: cell.address.trim() });
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (
        range.action === "clear" &&
        range.address?.trim() &&
        (range.clear?.clearType === "All" ||
          range.clear?.clearType === "Contents" ||
          range.clear?.clearType === undefined)
      ) {
        clearedRanges.push({ sheetName, address: range.address.trim() });
      }
    }
  }
  if (clearedRanges.length === 0) return;

  const isCleared = (sheetName: string, address: string): boolean =>
    clearedRanges.some(
      (cleared) =>
        cleared.sheetName.toLowerCase() === sheetName.toLowerCase() &&
        addressContains(cleared.address, address),
    );

  accumulators.fdsFormulaWriteTargets = (
    accumulators.fdsFormulaWriteTargets ?? []
  ).filter((target) => !isCleared(target.sheetName, target.address));

  for (const key of Object.keys(accumulators.fdsFormulaWriteAttempts ?? {})) {
    const separator = key.indexOf("!");
    if (separator < 0) continue;
    const sheetName = key.slice(0, separator);
    const address = key.slice(separator + 1);
    if (isCleared(sheetName, address)) {
      delete accumulators.fdsFormulaWriteAttempts?.[key];
    }
  }
}

function recordFdsDateAxisWriteTargets(
  accumulators: ExecutorToolRuntimeContext["accumulators"],
  modifications: WorkbookModification,
): void {
  accumulators.fdsDateAxisWriteTargets ??= [];
  const record = (
    sheetName: string,
    address: string,
    kind: "serial" | "key",
  ): void => {
    const existing = accumulators.fdsDateAxisWriteTargets?.find(
      (target) =>
        target.sheetName.toLowerCase() === sheetName.toLowerCase() &&
        normalizeAddress(target.address) === normalizeAddress(address),
    );
    if (existing) {
      existing.kind = kind;
      return;
    }
    accumulators.fdsDateAxisWriteTargets?.push({ sheetName, address, kind });
  };

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName =
      typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;
    for (const cell of worksheet.cells ?? []) {
      const formula = isFdsDateAxisFormula(cell.formula)
        ? cell.formula
        : cell.value;
      const kind = fdsDateAxisKind(formula);
      if (
        cell.action === "set" &&
        typeof cell.address === "string" &&
        cell.address.trim() &&
        kind
      ) {
        record(sheetName, cell.address.trim(), kind);
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (
        range.action !== "fill" ||
        typeof range.address !== "string" ||
        !range.address.trim()
      ) {
        continue;
      }
      const writeCells = enumerateRangeWriteCells(
        range.address,
        range.formulas,
        range.values,
      );
      if (writeCells) {
        for (const writeCell of writeCells) {
          if (writeCell.fdsDateAxisKind) {
            record(
              sheetName,
              writeCell.address,
              writeCell.fdsDateAxisKind,
            );
          }
        }
      } else {
        const kind =
          matrixFdsDateAxisKind(range.formulas) ??
          matrixFdsDateAxisKind(range.values);
        if (kind) record(sheetName, range.address.trim(), kind);
      }
    }
  }
}

interface ProtectedFdsFormulaOverwrite {
  sheetName: string;
  protectedAddress: string;
  writeAddress: string;
}

function findProtectedFdsFormulaOverwrite(
  ctx: ExecutorToolRuntimeContext,
  modifications: WorkbookModification,
): ProtectedFdsFormulaOverwrite | undefined {
  const protectedTargets = ctx.accumulators.fdsFormulaWriteTargets ?? [];
  if (protectedTargets.length === 0) return undefined;

  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = typeof worksheet.sheetName === "string" ? worksheet.sheetName.trim() : "";
    if (!sheetName) continue;

    for (const cell of worksheet.cells ?? []) {
      if (
        cell.action !== "set" ||
        typeof cell.address !== "string" ||
        !cell.address.trim() ||
        !hasNonBlankWriteContent([cell.formula, cell.value]) ||
        isFdsBoundFormula(cell.formula) ||
        isFdsBoundFormula(cell.value)
      ) {
        continue;
      }
      const protectedTarget = protectedTargets.find(
        (target) =>
          target.sheetName.toLowerCase() === sheetName.toLowerCase() &&
          addressesOverlap(target.address, cell.address),
      );
      if (protectedTarget) {
        if (
          isAuthorizedUnavailableFdsWrite(
            ctx,
            sheetName,
            cell.address,
            cell.formula,
            cell.value,
          )
        ) {
          continue;
        }
        return {
          sheetName,
          protectedAddress: protectedTarget.address,
          writeAddress: cell.address,
        };
      }
    }

    for (const range of worksheet.ranges ?? []) {
      if (
        range.action !== "fill" ||
        typeof range.address !== "string" ||
        !range.address.trim() ||
        !hasNonBlankWriteContent([range.formulas, range.values])
      ) {
        continue;
      }
      const sheetTargets = protectedTargets.filter(
        (target) => target.sheetName.toLowerCase() === sheetName.toLowerCase(),
      );
      if (sheetTargets.length === 0) continue;

      const writeCells = enumerateRangeWriteCells(
        range.address,
        range.formulas,
        range.values,
      );
      if (writeCells) {
        for (const writeCell of writeCells) {
          if (!writeCell.hasNonBlankContent || writeCell.hasFdsFormula) continue;
          const protectedTarget = sheetTargets.find((target) =>
            addressesOverlap(target.address, writeCell.address),
          );
          if (protectedTarget) {
            if (
              isAuthorizedUnavailableFdsWrite(
                ctx,
                sheetName,
                writeCell.address,
                writeCell.formula,
                writeCell.value,
              )
            ) {
              continue;
            }
            return {
              sheetName,
              protectedAddress: protectedTarget.address,
              writeAddress: writeCell.address,
            };
          }
        }
        continue;
      }

      const protectedTarget = sheetTargets.find((target) =>
        addressesOverlap(target.address, range.address),
      );
      if (protectedTarget) {
        // If individual cells cannot be mapped, do not let one FDS entry
        // exempt unrelated static content elsewhere in the same write.
        return {
          sheetName,
          protectedAddress: protectedTarget.address,
          writeAddress: range.address,
        };
      }
    }
  }

  return undefined;
}

function matrixHasFdsFormula(values: unknown): boolean {
  return (
    Array.isArray(values) &&
    values.some(
      (row) => Array.isArray(row) && row.some((value) => isFdsBoundFormula(value)),
    )
  );
}

function matrixFdsDateAxisKind(
  values: unknown,
): "serial" | "key" | undefined {
  if (!Array.isArray(values)) return undefined;
  let foundKey = false;
  for (const row of values) {
    if (!Array.isArray(row)) continue;
    for (const value of row) {
      const kind = fdsDateAxisKind(value);
      if (kind === "serial") return "serial";
      if (kind === "key") foundKey = true;
    }
  }
  return foundKey ? "key" : undefined;
}

function isFdsDateAxisFormula(value: unknown): value is string {
  return fdsDateAxisKind(value) !== undefined;
}

function fdsDateAxisKind(value: unknown): "serial" | "key" | undefined {
  if (!isFdsBoundFormula(value) || !/\.DATES\b/i.test(value)) {
    return undefined;
  }
  return /\bJULIAN\s*\(/i.test(value) ? "serial" : "key";
}

interface RangeWriteCell {
  address: string;
  hasNonBlankContent: boolean;
  hasFdsFormula: boolean;
  formula?: unknown;
  value?: unknown;
  fdsDateAxisKind?: "serial" | "key";
}

function enumerateRangeWriteCells(
  address: string,
  formulas: unknown,
  values: unknown,
): RangeWriteCell[] | undefined {
  const parsedRange = parseCellRange(address);
  if (!parsedRange) return undefined;
  const formulaRows = Array.isArray(formulas) ? formulas : [];
  const valueRows = Array.isArray(values) ? values : [];
  const rowCount = Math.max(formulaRows.length, valueRows.length);
  const cells: RangeWriteCell[] = [];

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const formulaRow = Array.isArray(formulaRows[rowIndex]) ? formulaRows[rowIndex] : [];
    const valueRow = Array.isArray(valueRows[rowIndex]) ? valueRows[rowIndex] : [];
    const columnCount = Math.max(formulaRow.length, valueRow.length);
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
      const formula = formulaRow[columnIndex];
      const value = valueRow[columnIndex];
      const fdsFormula = isFdsBoundFormula(formula)
        ? formula
        : isFdsBoundFormula(value)
          ? value
          : undefined;
      const dateAxisKind = fdsDateAxisKind(fdsFormula);
      cells.push({
        address:
          `${excelColumnName(parsedRange.startColumn + columnIndex)}` +
          `${parsedRange.startRow + rowIndex + 1}`,
        hasNonBlankContent: hasNonBlankWriteContent([formula, value]),
        hasFdsFormula: fdsFormula !== undefined,
        ...(formula !== undefined ? { formula } : {}),
        ...(value !== undefined ? { value } : {}),
        ...(dateAxisKind ? { fdsDateAxisKind: dateAxisKind } : {}),
      });
    }
  }
  return cells;
}

function isAuthorizedUnavailableFdsWrite(
  ctx: ExecutorToolRuntimeContext,
  sheetName: string,
  address: string,
  formula: unknown,
  value: unknown,
): boolean {
  if (
    hasNonBlankWriteContent(formula) ||
    typeof value !== "string" ||
    value.trim().toLowerCase() !== "n/a"
  ) {
    return false;
  }
  const normalizedAddress = normalizeAddress(address);
  const parsedAddress = parseCellRange(normalizedAddress);
  if (
    !parsedAddress ||
    parsedAddress.startRow !== parsedAddress.endRow ||
    parsedAddress.startColumn !== parsedAddress.endColumn
  ) {
    return false;
  }
  return (ctx.accumulators.unavailableFdsFormulaTargets ?? []).some(
    (target) =>
      target.sheetName.trim().toLowerCase() === sheetName.trim().toLowerCase() &&
      normalizeAddress(target.address) === normalizedAddress,
  );
}

function hasNonBlankWriteContent(values: unknown): boolean {
  if (Array.isArray(values)) return values.some(hasNonBlankWriteContent);
  return values !== null && values !== undefined && values !== "";
}

interface ParsedCellRange {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}

function addressesOverlap(left: string, right: string): boolean {
  const parsedLeft = parseCellRange(left);
  const parsedRight = parseCellRange(right);
  if (!parsedLeft || !parsedRight) {
    return normalizeAddress(left) === normalizeAddress(right);
  }
  return (
    parsedLeft.startRow <= parsedRight.endRow &&
    parsedLeft.endRow >= parsedRight.startRow &&
    parsedLeft.startColumn <= parsedRight.endColumn &&
    parsedLeft.endColumn >= parsedRight.startColumn
  );
}

function addressContains(container: string, candidate: string): boolean {
  const parsedContainer = parseCellRange(container);
  const parsedCandidate = parseCellRange(candidate);
  if (!parsedContainer || !parsedCandidate) {
    return normalizeAddress(container) === normalizeAddress(candidate);
  }
  return (
    parsedContainer.startRow <= parsedCandidate.startRow &&
    parsedContainer.endRow >= parsedCandidate.endRow &&
    parsedContainer.startColumn <= parsedCandidate.startColumn &&
    parsedContainer.endColumn >= parsedCandidate.endColumn
  );
}

function parseCellRange(address: string): ParsedCellRange | undefined {
  const bareAddress = address.trim().split("!").pop()?.replace(/\$/g, "") ?? "";
  const match = bareAddress.match(
    /^([A-Za-z]+)(\d+)(?::([A-Za-z]+)(\d+))?$/,
  );
  if (!match) return undefined;
  const startColumn = excelColumnIndex(match[1]);
  const startRow = Number(match[2]) - 1;
  const endColumn = excelColumnIndex(match[3] || match[1]);
  const endRow = Number(match[4] || match[2]) - 1;
  if (
    !Number.isSafeInteger(startColumn) ||
    !Number.isSafeInteger(endColumn) ||
    !Number.isSafeInteger(startRow) ||
    !Number.isSafeInteger(endRow) ||
    startColumn < 0 ||
    endColumn < 0 ||
    startColumn > 16_383 ||
    endColumn > 16_383 ||
    startRow < 0 ||
    endRow < 0 ||
    startRow > 1_048_575 ||
    endRow > 1_048_575
  ) {
    return undefined;
  }
  return {
    startRow: Math.min(startRow, endRow),
    endRow: Math.max(startRow, endRow),
    startColumn: Math.min(startColumn, endColumn),
    endColumn: Math.max(startColumn, endColumn),
  };
}

function excelColumnIndex(column: string): number {
  let value = 0;
  for (const character of column.toUpperCase()) {
    value = value * 26 + character.charCodeAt(0) - 64;
  }
  return value - 1;
}

function excelColumnName(columnIndex: number): string {
  let value = columnIndex + 1;
  let name = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }
  return name;
}

function normalizeAddress(address: string): string {
  return address.trim().replace(/\$/g, "").toUpperCase();
}

function isFormulaWrite(value: unknown): boolean {
  return typeof value === "string" && value.trimStart().startsWith("=");
}

function matrixHasFormulaWrite(values: unknown): boolean {
  return (
    Array.isArray(values) &&
    values.some((row) => Array.isArray(row) && row.some(isFormulaWrite))
  );
}

/**
 * Records an exception thrown by the host apply callback.
 *
 * Like {@link recordApplyResult}, this does not change step status; the model
 * sees the failure via the tool result and decides how to record it through
 * `the internal step lifecycle`. Run-level writes keep a trace line for the final verifier.
 *
 * @param ctx - Runtime context with mutable accumulators.
 * @param input - Normalized tool input.
 * @param step - Associated step, if any.
 * @param message - Exception message to record.
 */
function recordThrownApplyFailure(
  ctx: ExecutorToolRuntimeContext,
  input: ModifyWorkbookInput,
  step: ExecutorStepState | undefined,
  message: string,
): void {
  if (!step) {
    ctx.accumulators.stepResults.push(
      `modify_workbook. ${message || input.modifications.message || "Workbook modifications failed"}`,
    );
  }
}
